import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { User } from '../types/alarm';
import { upgradeService, clearAdminStatsListLocalCache, type AdminStatsListMember } from '../services/upgradeService';
import { licenseService } from '../services/licenseService';
import { announcementService, Announcement } from '../services/announcementService';
import { termsPrivacyService, TermsPrivacy } from '../services/termsPrivacyService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { fileToWebpBlob, IMAGE_WEBP_EXT, IMAGE_WEBP_MIME } from '../utils/imageCompress';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { storage, auth, functions, db } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';

/** 관리자 통계 모달 제목 (세그먼트 키와 동일 규칙은 Cloud Function listUsersForStatsSegment) */
const USER_STATS_SEGMENT_TITLES: Record<string, string> = {
  total: '전체 사용자',
  admin: '관리자',
  free: '프리',
  basic: '베이직',
  standard: '스탠다드',
  pro: '프로',
  basicIndividualPaid: '베이직 · 개별 유료',
  basicGroupCoupon: '베이직 · 그룹 유료 쿠폰',
  basicEventCoupon: '베이직 · 이벤트 쿠폰',
  standardIndividualPaid: '스탠다드 · 개별 유료',
  standardGroupCoupon: '스탠다드 · 그룹 유료 쿠폰',
  standardEventCoupon: '스탠다드 · 이벤트 쿠폰',
  proIndividualPaid: '프로 · 개별 유료',
  proGroupCoupon: '프로 · 그룹 유료 쿠폰',
  proEventCoupon: '프로 · 이벤트 쿠폰',
};

function formatAdminLicenseField(v: unknown): string {
  if (v == null || v === '') return '-';
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('ko-KR');
  }
  if (typeof v === 'boolean') return v ? '예' : '아니오';
  return String(v);
}

const billingSettingsDocRef = doc(db, 'appSettings', 'billing');

/** 관리자용 테스트 계정 (Cloud Function createTestAccount와 동일) */
const ADMIN_TEST_ACCOUNT_EMAIL = 'test@edukodi.com';
const ADMIN_TEST_ACCOUNT_PASSWORD = 'test1234';

interface AdminPageProps {
  user: User;
  onBack: () => void;
}

const Container = styled.div`
  min-height: 100vh;
  background: var(--color-bg);
  padding: 20px;
  padding-bottom: 120px;
`;

const AdminWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  color: var(--color-text-primary);
  margin: 0;
  font-size: 1.8rem;
  font-weight: 600;
`;

const BackButton = styled.button`
  background-color: var(--color-accent-text);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--color-accent);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  /* 다크모드 스타일 */
  :root:not([data-theme='light']) & {
    background-color: var(--color-surface);
    color: var(--color-text-primary);
    border: 1px solid var(--color-border);

    &:hover {
      background-color: var(--color-panel);
      border-color: var(--color-text-muted);
    }
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 24px;
  margin-bottom: 24px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/** 1·2열 전체를 차지하는 행 (사용자 통계 등) */
const AdminGridFullRow = styled.div`
  grid-column: 1 / -1;
`;

const AdminGridCol2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  grid-column: 1 / -1;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const CardTitle = styled.h2`
  color: var(--color-text-primary);
  margin: 0 0 20px 0;
  font-size: 1.3rem;
  font-weight: 600;
`;

const UserStatsLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
  width: 100%;
`;

const StatItem = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 8px 10px;
  background-color: var(--color-panel);
  border-radius: 8px;
  border: 1px solid var(--color-border);
  gap: 2px;
`;

const StatNumber = styled.div`
  font-size: 1.1rem;
  font-weight: bold;
  color: var(--color-accent-text);
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: var(--color-text-secondary);
  font-weight: 500;
`;

const UserStatsBlockLabel = styled(StatLabel)`
  font-size: 0.75rem;
  line-height: 1.2;
`;

const UserStatsBlockNumber = styled(StatNumber)`
  font-size: 1rem;
`;

const ClickableStatItem = styled.button.attrs({ type: 'button' })`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 8px 10px;
  background-color: var(--color-panel);
  border-radius: 8px;
  border: 1px solid var(--color-border);
  gap: 2px;
  cursor: pointer;
  appearance: none;
  font: inherit;
  color: inherit;
  text-align: center;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  &:hover {
    background-color: var(--color-accent-soft);
    border-color: var(--color-accent-strong);
  }
`;

const ClickableTotalStatItem = styled.button.attrs({ type: 'button' })`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  background-color: var(--color-accent-soft);
  border-radius: 8px;
  border: 1px solid var(--color-accent-strong);
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  appearance: none;
  font: inherit;
  color: inherit;
  text-align: left;
  transition: background-color 0.15s ease, border-color 0.15s ease, filter 0.15s ease;
  &:hover {
    filter: brightness(0.97);
    border-color: var(--color-accent-text);
  }
`;

const UserStatsPlanSourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  width: 100%;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const UserStatsPlanSourceCard = styled.div`
  position: relative;
  margin-top: 14px;
  padding: 18px 0 0;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-panel);
  overflow: visible;
`;

const UserStatsPlanSourceTitle = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 6px 18px;
  border-radius: 999px;
  background: var(--color-accent-strong);
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);

  /* 다크모드: 메인 초록 → 흰 배경, 글자는 검정 */
  :root:not([data-theme='light']) & {
    background: #ffffff;
    color: #111111;
    border: 1px solid var(--color-border);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
  }
`;

const UserStatsPlanSourceButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: stretch;
`;

const UserStatsPlanSourceButton = styled.button.attrs({ type: 'button' })`
  min-width: 0;
  border: none;
  border-right: 1px solid var(--color-border);
  background: transparent;
  cursor: pointer;
  padding: 10px 8px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: inherit;
  font: inherit;
  transition: background-color 0.15s ease;

  &:last-child {
    border-right: none;
  }

  &:hover {
    background: var(--color-accent-soft);
  }
`;

const UserStatsPlanSourceButtonLabel = styled.div`
  font-size: 0.76rem;
  line-height: 1.25;
  color: var(--color-text-secondary);
  font-weight: 600;
  text-align: center;
`;

const UserStatsPlanSourceButtonCount = styled.div`
  font-size: 1rem;
  line-height: 1.2;
  color: var(--color-accent-text);
  font-weight: 700;
`;

const UserStatsMemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
`;

const UserStatsMemberCard = styled.div`
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 12px 14px;
  background: var(--color-panel);
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px 16px;
  align-items: center;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const UserStatsMemberFields = styled.div`
  min-width: 0;
  display: grid;
  gap: 10px;
  width: 100%;
`;

const UserStatsMemberRow = styled.div`
  display: grid;
  align-items: center;
  gap: 14px;
  grid-template-columns: minmax(140px, 180px) minmax(220px, 320px) minmax(260px, 1fr);
  justify-items: start;
  width: 100%;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const UserStatsSpacer = styled.span`
  width: 100%;
`;

const UserStatsPrimaryText = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: var(--color-text-primary);
`;

const UserStatsMutedText = styled.span`
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.45;
  word-break: break-all;
`;

const UserStatsAccentText = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: var(--color-accent-text);
  line-height: 1.45;
`;

const UserStatsReloadSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
`;

const UserStatsReloadHint = styled.p`
  margin: 0;
  max-width: 440px;
  text-align: center;
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 1.5;
`;

const UserStatsSearchSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 16px;
`;

const UserStatsSearchCount = styled.div`
  margin-top: 12px;
  font-size: 13px;
  color: var(--color-text-secondary);
  text-align: center;
`;

const UserStatsSearchInput = styled.input`
  width: 100%;
  max-width: 480px;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  box-sizing: border-box;
  text-align: center;

  &::placeholder {
    text-align: center;
  }
`;

const LicenseHistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  th,
  td {
    border: 1px solid var(--color-border);
    padding: 6px 8px;
    text-align: left;
    word-break: break-word;
    vertical-align: top;
  }
  th {
    background: var(--color-panel);
    color: var(--color-text-secondary);
    font-weight: 600;
    white-space: nowrap;
  }
`;

const LicenseDetailButton = styled.button`
  justify-self: end;
  align-self: center;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  &:hover {
    border-color: var(--color-accent-strong);
    background: var(--color-accent-soft);
  }
  @media (max-width: 720px) {
    justify-self: end;
  }
`;

const GeneratedCodeDisplay = styled.div`
  background-color: transparent;
  border: 2px dashed var(--color-border);
  border-radius: 6px;
  padding: 10px 12px;
  margin-top: 8px;
  min-height: 50px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const EmptyCodeDisplay = styled.div`
  background-color: transparent;
  border: 2px dashed var(--color-border);
  border-radius: 6px;
  padding: 10px 12px;
  text-align: center;
  margin-top: 8px;
  min-height: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #999;
  font-size: 0.85rem;
`;

const CodeText = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 1rem;
  font-weight: bold;
  color: var(--color-accent-text);
  letter-spacing: 1.5px;
  flex: 1;
  text-align: left;
`;

const CopyButton = styled.button`
  background: var(--color-surface);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-border);
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
  }
`;

const CodeActionButtons = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  padding: 12px;
  margin-top: 12px;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const InitializeButton = styled.button`
  background: var(--color-surface);
  color: var(--color-text-primary);
  border: 2px solid var(--color-border);
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  margin-top: 16px;
  width: 100%;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-border);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background: var(--color-panel);
    color: #6c757d;
    border-color: var(--color-border);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const Description = styled.p`
  margin: 0 0 12px 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-text-gray);
`;

const AdminTestAccountBanner = styled.div`
  background: var(--color-panel);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
`;

const AdminTestAccountBannerTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 10px;
`;

const AdminTestAccountBannerRow = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-text-secondary);
  font-family: ui-monospace, 'SF Mono', 'Courier New', monospace;

  strong {
    font-family: inherit;
    color: var(--color-text-primary);
    font-weight: 600;
    margin-right: 8px;
  }
`;

const AdminTestAccountBannerHint = styled.p`
  margin: 12px 0 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--color-text-muted);
`;

const AdminInputRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

const AdminInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.2);
  }

  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const AdminInputLabel = styled.div`
  font-size: 12px;
  color: var(--color-text-secondary);
  margin-bottom: 6px;
`;

const AdminActionButton = styled.button`
  background: var(--color-surface);
  color: var(--color-text-primary);
  border: 2px solid var(--color-border);
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-border);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    background: var(--color-panel);
    color: #6c757d;
    border-color: var(--color-border);
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const EventCodePlanButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const EventCodePlanButton = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 10px 12px;
  border-radius: 8px;
  border: 2px solid ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  background: ${props => props.$isActive ? 'var(--color-accent-soft)' : 'var(--color-surface)'};
  color: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-text-secondary)'};
  font-weight: ${props => props.$isActive ? '700' : '600'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-accent-strong);
    color: var(--color-accent-strong);
    background: var(--color-accent-soft);
  }
`;

const EventCodeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
`;

const EventCodeItem = styled.div`
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 12px;
  background: var(--color-surface);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const EventCodeMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: var(--color-text-secondary);
  font-size: 12px;
`;

/** 활성 X/Y 표시용 눈에 띄는 네모 블럭 */
const ActivatedCountBlock = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: 6px 14px;
  margin: 6px auto;
  background: white;
  color: var(--color-text-primary);
  font-size: 14px;
  font-weight: 700;
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  align-self: center;

  :root:not([data-theme='light']) & {
    background: var(--color-panel);
  }
`;

const EventCodeValue = styled.div`
  font-weight: 700;
  color: var(--color-text-primary);
  font-size: 14px;
`;

const EventCodeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EventCodeStatus = styled.span<{ $closed?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.$closed ? '#8a1f2f' : '#0b5a34'};
  background: ${props => props.$closed ? 'rgba(220, 53, 69, 0.12)' : 'rgba(25, 135, 84, 0.12)'};
`;

const EventCodeActionButton = styled.button`
  border: 1px solid var(--color-border);
  background: var(--color-panel);
  color: var(--color-text-primary);
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-accent-strong);
    background: var(--color-surface);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EventCodeCopyButton = styled.button`
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--color-accent-strong);
    color: var(--color-accent-strong);
    background: var(--color-panel);
  }
`;

const EventCodeDeleteButton = styled(EventCodeActionButton)`
  border-color: rgba(220, 53, 69, 0.6);
  color: #dc3545;

  &:hover {
    border-color: #dc3545;
    background: rgba(220, 53, 69, 0.08);
  }
`;

const PaymentModeButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const PaymentModeButton = styled.button.attrs({ type: 'button' })<{ $isActive: boolean }>`
  flex: 1;
  padding: 12px 24px;
  border: 2px solid ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  background: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.$isActive ? 'white' : 'var(--color-text-primary)'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-panel)'};
    border-color: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-text-muted)'};
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const PaymentModeDescription = styled.div`
  padding: 12px 16px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--color-text-secondary);
  
  span {
    display: block;
  }
`;

const EditorToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  background: var(--color-panel);
  border: 2px solid var(--color-border);
  border-radius: 8px 8px 0 0;
  border-bottom: none;
  margin-bottom: 0;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0;
  width: 100%;
  margin-bottom: 16px;
  border-bottom: 2px solid var(--color-border);
`;

const TabButton = styled.button<{ $isActive: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 12px 24px;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.$isActive ? 'var(--color-accent-strong)' : 'transparent'};
  color: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-text-gray)'};
  font-size: 16px;
  font-weight: ${props => props.$isActive ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: -2px;

  &:hover {
    color: var(--color-accent-text);
    background: var(--color-accent-soft);
  }

  &:focus-visible {
    outline: 2px solid black;
    outline-offset: 2px;
  }
`;

const TermsBlock = styled.div`
  padding: 16px;
  background: var(--color-panel);
  border: 2px solid var(--color-border);
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--color-accent-strong);
    background: var(--color-surface);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
`;

const TermsBlockTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 8px;
`;

const TermsBlockDate = styled.div`
  font-size: 12px;
  color: var(--color-text-gray);
`;

const TermsBlockActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  transition: all 0.2s;

  svg {
    flex-shrink: 0;
    display: block;
  }

  &:hover {
    background: rgba(220, 53, 69, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #dc3545;
    outline-offset: 2px;
  }
`;

/** ClassManageModal 등과 동일한 휴지통 아이콘 (stroke 기반) */
const TermsDeleteTrashSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const OldTermsBlock = styled.div`
  padding: 16px;
  background: var(--color-panel);
  border: 2px solid var(--color-border);
  border-radius: 8px;
  margin-bottom: 12px;
  opacity: 0.7;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 1;
    border-color: var(--color-accent-strong);
    background: var(--color-surface);
  }
`;

const OldTermsBlockTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin-bottom: 8px;
`;

const OldTermsBlockDate = styled.div`
  font-size: 11px;
  color: var(--color-text-gray);
`;

const DeleteConfirmModal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10003;
  padding: 20px;
`;

const DeleteConfirmContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
`;

const DeleteConfirmTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const DeleteConfirmMessage = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.6;
`;

const DeleteConfirmButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const DeleteConfirmButton = styled.button<{ $danger?: boolean }>`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid var(--color-border);
  
  ${props => props.$danger ? `
    background: #dc3545;
    color: white;
    border-color: #dc3545;
  
    &:hover:not(:disabled) {
      background: #c82333;
      border-color: #c82333;
    }
  ` : `
    background: var(--color-surface);
    color: var(--color-text-primary);

    &:hover:not(:disabled) {
      background: var(--color-panel);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${props => props.$danger ? '#dc3545' : 'var(--color-accent-strong)'};
    outline-offset: 2px;
  }
`;

const CreateButton = styled.button`
  width: 100%;
  padding: 16px;
  background: var(--color-surface);
  border: 2px dashed var(--color-border);
  border-radius: 8px;
  color: var(--color-text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    border-color: var(--color-accent-strong);
    color: var(--color-accent-text);
    background: var(--color-panel);
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
`;

const AddButton = styled.button`
  padding: 12px 20px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: var(--color-accent-text);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AnnouncementBlock = styled.div`
  background: var(--color-panel);
  border: 2px solid var(--color-border);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
`;

const AnnouncementHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const AnnouncementMeta = styled.div`
  flex: 1;
`;

const AnnouncementDate = styled.div`
  font-size: 12px;
  color: var(--color-text-secondary);
  margin-bottom: 4px;
`;

const AnnouncementTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const AnnouncementActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionIcon = styled.svg`
  width: 20px;
  height: 20px;
  color: var(--color-text-secondary);
  fill: none;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: var(--color-accent-strong);
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: var(--color-border);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.span`
  font-size: 14px;
  color: var(--color-text-secondary);
`;

const ResendModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10004;
  padding: 20px;
`;

const ResendModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 420px;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-shell-border);
`;

const ResendModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const ResendModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const ResendModalButton = styled.button<{ $primary?: boolean }>`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 2px solid var(--color-border);
  background: ${props => props.$primary ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.$primary ? '#fff' : 'var(--color-text-primary)'};

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ListModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10005;
  padding: 20px;
`;

const ListModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 96%;
  max-width: 1100px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-shell-border);
`;

const ListModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
`;

const ListModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text-primary);
`;

const ListModalBody = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
`;

const NestedListModalOverlay = styled(ListModalOverlay)`
  z-index: 10009;
`;

const TermsModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10002;
  padding: 20px;
`;

const TermsModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
  overflow: hidden;
`;

const TermsModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;

  h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: var(--color-text-primary);
  }
`;

const TermsModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: var(--color-text-gray);
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;

  &:hover {
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const TermsModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const TermsModalEditor = styled.div`
  width: 100%;
  min-height: 500px;
  padding: 16px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  background: var(--color-surface);
  color: var(--color-text-primary);
  box-sizing: border-box;
  overflow-y: auto;
  line-height: 1.6;
  direction: ltr;
  text-align: left;

  /* 다크모드에서 흰색 텍스트 적용 */
  :root:not([data-theme='light']) & {
    color: #ffffff !important;

    /* 모든 텍스트 요소의 색상을 흰색으로 강제 변환 */
    *,
    *::before,
    *::after {
      color: #ffffff !important;
    }

    /* 복사된 검정색 텍스트를 흰색으로 변환 (더 넓은 범위) */
    [style*="color:black"],
    [style*="color:#000"],
    [style*="color:#000000"],
    [style*="color:#000000;"],
    [style*="color:rgb(0,0,0)"],
    [style*="color:rgba(0,0,0,1)"],
    [style*="color:rgba(0,0,0,1);"] {
      color: #ffffff !important;
    }

    /* font 태그의 검정색을 흰색으로 변환 */
    font[color="black"],
    font[color="#000"],
    font[color="#000000"] {
      color: #ffffff !important;
    }

    /* 모든 텍스트 노드에 흰색 적용 */
    p, h1, h2, h3, h4, h5, h6, span, div, strong, em, li, a {
      color: #ffffff !important;
    }

    /* 인라인 스타일로 지정된 검정색을 모두 흰색으로 */
    *[style*="color: black"],
    *[style*="color: #000"],
    *[style*="color: #000000"],
    *[style*="color: rgb(0, 0, 0)"],
    *[style*="color: rgba(0, 0, 0, 1)"] {
      color: #ffffff !important;
    }
  }

  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }

  &[contenteditable="true"]:empty:before {
    content: attr(data-placeholder);
    color: var(--color-text-muted);

    /* 다크모드에서 placeholder 흰색 적용 */
    :root:not([data-theme='light']) & {
      color: rgba(255, 255, 255, 0.6);
    }
  }

  /* 공지사항 편집 시 링크 버튼: 라이트 모드에서 기본 파란색 대신 텍스트 색상 사용 */
  :root[data-theme='light'] & a[data-announcement-link="button"] {
    color: var(--color-text-primary) !important;
  }

  & img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 8px auto;
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 16px 0 8px 0;
    font-weight: 600;
  }

  p {
    margin: 8px 0;
  }

  ul, ol {
    margin: 8px 0;
    padding-left: 24px;
  }
`;

const ToolbarButton = styled.button`
  background: var(--color-surface);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: var(--color-accent-soft);
    border-color: var(--color-accent-strong);
  }

  &:active {
    transform: scale(0.95);
  }

  &.active {
    background: var(--color-accent-strong);
    color: white;
    border-color: var(--color-accent-strong);
  }
`;

const FontSizeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-surface);
  height: 32px;
  padding: 0 4px;
`;

const FontSizeInput = styled.input`
  background: transparent;
  border: none;
  color: var(--color-text-primary);
  font-size: 14px;
  font-weight: 500;
  width: 50px;
  text-align: center;
  padding: 0;
  outline: none;
  
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const FontSizeLabel = styled.span`
  color: var(--color-text-primary);
  font-size: 12px;
  font-weight: 500;
  margin-right: 2px;
`;

const FontSizeButton = styled.button`
  background: transparent;
  border: none;
  color: var(--color-text-primary);
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 6px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  border-radius: 2px;
  min-width: 24px;

  &:hover {
    background: var(--color-accent-soft);
  }

  &:active {
    background: var(--color-accent-strong);
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AnnouncementLinkModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10003;
`;

const LinkModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  box-shadow: var(--modal-shadow-large);
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  :root:not([data-theme='light']) & {
    border: var(--modal-shell-border);
  }
`;

const LinkModalTitle = styled.h3`
  margin: 0;
  color: var(--color-text-primary);
  font-size: 1.2rem;
  font-weight: 600;
`;

const LinkModalInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  background: var(--color-surface);
  color: var(--color-text-primary);
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }
`;

const LinkModalLabel = styled.label`
  color: var(--color-text-primary);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  display: block;
`;

const LinkModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const LinkModalButton = styled.button<{ $primary?: boolean }>`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid var(--color-border);
  
  ${props => props.$primary ? `
    background: var(--color-accent-strong);
    color: white;
    border-color: var(--color-accent-strong);
  
  &:hover {
      background: var(--color-accent);
    }
  ` : `
    background: var(--color-surface);
    color: var(--color-text-primary);
    
    &:hover {
      background: var(--color-panel);
    }
  `}

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ToolbarColorInput = styled.input`
  width: 40px;
  height: 32px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  cursor: pointer;
  padding: 2px;
  background: var(--color-surface);

  &:hover {
    border-color: var(--color-accent-strong);
  }
`;


const SaveButton = styled.button`
  background: var(--color-accent-strong);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  width: 100%;

  &:hover {
    background: var(--color-accent);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const AdminPage: React.FC<AdminPageProps> = ({ user, onBack }) => {
  const [stats, setStats] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [recomputingStats, setRecomputingStats] = useState(false);
  const [userStatsListModal, setUserStatsListModal] = useState<{
    segment: string;
    title: string;
    members: AdminStatsListMember[];
    loading: boolean;
    fromCache?: boolean;
  } | null>(null);
  const [userStatsListSearch, setUserStatsListSearch] = useState('');
  const [userStatsListRefreshing, setUserStatsListRefreshing] = useState(false);
  const [licenseHistoryModal, setLicenseHistoryModal] = useState<AdminStatsListMember | null>(null);
  const [eventCodeName, setEventCodeName] = useState('');
  const [eventCodeDuration, setEventCodeDuration] = useState('1');
  const [eventCodeMaxUsers, setEventCodeMaxUsers] = useState('10');
  const [eventCodePlan, setEventCodePlan] = useState<'basic' | 'standard' | 'pro'>('basic');
  const [eventCodeGenerating, setEventCodeGenerating] = useState(false);
  const [eventCodeValue, setEventCodeValue] = useState('');
  const [eventCodeError, setEventCodeError] = useState('');
  const [eventCodes, setEventCodes] = useState<any[]>([]);
  const [loadingEventCodes, setLoadingEventCodes] = useState(false);
  const [eventCodeToggling, setEventCodeToggling] = useState<Set<string>>(new Set());
  const [eventCodeDeleting, setEventCodeDeleting] = useState<Set<string>>(new Set());
  const [showEventCodeListModal, setShowEventCodeListModal] = useState(false);
  const [eventCodeCurrentPage, setEventCodeCurrentPage] = useState(1);
  const [groupCouponBatches, setGroupCouponBatches] = useState<any[]>([]);
  const [loadingGroupCouponBatches, setLoadingGroupCouponBatches] = useState(false);
  const [groupCouponRefunding, setGroupCouponRefunding] = useState<Set<string>>(new Set());
  const [groupCouponResending, setGroupCouponResending] = useState<Set<string>>(new Set());
  const [showGroupCouponListModal, setShowGroupCouponListModal] = useState(false);
  const [groupCouponCurrentPage, setGroupCouponCurrentPage] = useState(1);
  const [groupIssueName, setGroupIssueName] = useState('');
  const [groupIssueEmail, setGroupIssueEmail] = useState('');
  const [groupIssueDuration, setGroupIssueDuration] = useState('12');
  const [groupIssueQuantity, setGroupIssueQuantity] = useState('10');
  const [groupIssuePlan, setGroupIssuePlan] = useState<'basic' | 'standard' | 'pro'>('basic');
  const [groupIssueGenerating, setGroupIssueGenerating] = useState(false);
  const [groupIssueError, setGroupIssueError] = useState('');
  const [groupIssueBatchId, setGroupIssueBatchId] = useState('');
  const [groupIssueCode, setGroupIssueCode] = useState('');
  const [resendModalBatch, setResendModalBatch] = useState<{ batchId: string; targetEmail: string } | null>(null);
  const [resendModalEmail, setResendModalEmail] = useState('');
  const [deleteAnonymousLoading, setDeleteAnonymousLoading] = useState(false);
  const [deleteAnonymousMessage, setDeleteAnonymousMessage] = useState<string | null>(null);
  const [createTestAccountLoading, setCreateTestAccountLoading] = useState(false);
  const [createTestAccountMessage, setCreateTestAccountMessage] = useState<string | null>(null);
  const [resetTestAccountLicenseLoading, setResetTestAccountLicenseLoading] = useState(false);
  const [resetTestAccountLicenseMessage, setResetTestAccountLicenseMessage] = useState<string | null>(null);

  // 공지사항 목록 관리
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(true);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [announcementModalContent, setAnnouncementModalContent] = useState('');
  const [announcementModalTitle, setAnnouncementModalTitle] = useState('');
  const [announcementModalFontSize, setAnnouncementModalFontSize] = useState<number>(14);
  const [isAnnouncementModalLinkModalOpen, setIsAnnouncementModalLinkModalOpen] = useState(false);
  const [isUploadingAnnouncementModalImage, setIsUploadingAnnouncementModalImage] = useState(false);
  const [announcementModalLinkText, setAnnouncementModalLinkText] = useState('');
  const [announcementModalLinkUrl, setAnnouncementModalLinkUrl] = useState('');
  const [announcementCurrentPage, setAnnouncementCurrentPage] = useState(1);
  const [selectedAnnouncementIndex, setSelectedAnnouncementIndex] = useState<number | null>(null);
  const announcementsPerPage = 5;
  const announcementModalImageInputRef = useRef<HTMLInputElement>(null);
  const announcementModalSavedSelectionRef = useRef<Range | null>(null);
  const announcementModalEditingLinkRef = useRef<HTMLAnchorElement | null>(null);
  
  // 약관 및 개인정보처리방침 상태
  const [termsPrivacy, setTermsPrivacy] = useState<TermsPrivacy | null>(null);
  const [termsPrivacyHistory, setTermsPrivacyHistory] = useState<any>(null);
  const [, setTermsContent] = useState('');
  const [, setPrivacyContent] = useState('');
  const [loadingTermsPrivacy, setLoadingTermsPrivacy] = useState(false);
  const [savingTermsPrivacy, setSavingTermsPrivacy] = useState(false);
  const [activeTermsTab, setActiveTermsTab] = useState<'terms' | 'privacy'>('terms');
  const termsEditorRef = useRef<HTMLDivElement>(null);
  const privacyEditorRef = useRef<HTMLDivElement>(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isTermsModalReadOnly, setIsTermsModalReadOnly] = useState(false);
  const [editingTermsType, setEditingTermsType] = useState<'terms' | 'privacy'>('terms');
  const modalEditorRef = useRef<HTMLDivElement>(null);
  const announcementModalEditorRef = useRef<HTMLDivElement>(null);
  const [modalContent, setModalContent] = useState('');
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ type: 'terms' | 'privacy'; id: string } | null>(null);
  const [deletingItem, setDeletingItem] = useState(false);

  // PG 결제 모드·유료 플랜 노출: Firestore appSettings/billing + 로컬 캐시(localStorage). 로그인 사용자에게 실시간 반영.
  const [paymentMode, setPaymentMode] = useState<'test' | 'production'>(() => {
    const saved = localStorage.getItem('paymentMode');
    return saved === 'test' || saved === 'production' ? saved : 'test';
  });
  const [paidPaymentEnabled, setPaidPaymentEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('paidPaymentEnabled');
    return saved === 'true';
  });

  const handlePaymentModeChange = async (mode: 'test' | 'production') => {
    setPaymentMode(mode);
    localStorage.setItem('paymentMode', mode);
    try {
      await setDoc(
        billingSettingsDocRef,
        { paymentMode: mode, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (e) {
      console.error('PG 결제 모드 저장 실패:', e);
      alert('설정을 서버에 저장하지 못했습니다. 네트워크와 Firestore 규칙 배포를 확인해주세요.');
    }
  };

  const handlePaidPaymentEnabledChange = async (enabled: boolean) => {
    setPaidPaymentEnabled(enabled);
    localStorage.setItem('paidPaymentEnabled', enabled ? 'true' : 'false');
    try {
      await setDoc(
        billingSettingsDocRef,
        { paidPaymentEnabled: enabled, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (e) {
      console.error('유료 플랜 결제 활성화 저장 실패:', e);
      alert('설정을 서버에 저장하지 못했습니다. 네트워크와 Firestore 규칙 배포를 확인해주세요.');
    }
  };

  const listItemsPerPage = 10;

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const result = await upgradeService.getAdminStats(user.id);
      if (result.success && result.data) {
        setStats(result.data);
      } else {
        console.error('통계 로드 실패:', result.error);
      }
    } catch (error) {
      console.error('통계 로드 오류:', error);
    } finally {
      setLoadingStats(false);
    }
  }, [user.id]);

  const loadEventCodes = useCallback(async () => {
    setLoadingEventCodes(true);
    try {
      const result = await upgradeService.getEventCodes(user.id);
      if (result.success && result.codes) {
        setEventCodes(result.codes);
      } else {
        console.error('이벤트 코드 목록 조회 실패:', result.error);
      }
    } catch (error) {
      console.error('이벤트 코드 목록 조회 오류:', error);
    } finally {
      setLoadingEventCodes(false);
    }
  }, [user.id]);

  const loadGroupCouponBatches = useCallback(async () => {
    setLoadingGroupCouponBatches(true);
    try {
      const result = await upgradeService.getGroupCouponBatches(user.id);
      if (result.success && result.batches) {
        setGroupCouponBatches(result.batches);
      } else {
        console.error('그룹 이용권 배치 목록 조회 실패:', result.error);
      }
    } catch (error) {
      console.error('그룹 이용권 배치 목록 조회 오류:', error);
    } finally {
      setLoadingGroupCouponBatches(false);
    }
  }, [user.id]);

  const openUserStatsListModal = useCallback(
    async (segment: string, opts?: { bypassCache?: boolean }) => {
      const title = USER_STATS_SEGMENT_TITLES[segment] || segment;
      setUserStatsListSearch('');
      setLicenseHistoryModal(null);
      if (!opts?.bypassCache) {
        const cached = upgradeService.tryReadAdminStatsListLocalCache(segment);
        if (cached) {
          setUserStatsListModal({ segment, title, members: cached, loading: false, fromCache: true });
          return;
        }
      }
      setUserStatsListModal({ segment, title, members: [], loading: true, fromCache: false });
      const result = await upgradeService.listUsersForStatsSegment(user.id, segment, { bypassCache: opts?.bypassCache });
      if (!result.success || !result.members) {
        alert(result.error || '목록을 불러오지 못했습니다.');
        setUserStatsListModal(null);
        return;
      }
      setUserStatsListModal({
        segment,
        title,
        members: result.members,
        loading: false,
        fromCache: result.fromCache === true,
      });
    },
    [user.id]
  );

  const refreshUserStatsListFromServer = useCallback(
    async (segment: string) => {
      const title = USER_STATS_SEGMENT_TITLES[segment] || segment;
      setUserStatsListRefreshing(true);
      try {
        const result = await upgradeService.listUsersForStatsSegment(user.id, segment, { bypassCache: true });
        if (!result.success || !result.members) {
          alert(result.error || '목록을 불러오지 못했습니다.');
          return;
        }
        setUserStatsListModal((prev) =>
          prev && prev.segment === segment
            ? { segment, title, members: result.members!, loading: false, fromCache: false }
            : prev
        );
      } finally {
        setUserStatsListRefreshing(false);
      }
    },
    [user.id]
  );

  const closeUserStatsListModal = useCallback(() => {
    setUserStatsListModal(null);
    setUserStatsListSearch('');
    setLicenseHistoryModal(null);
  }, []);

  // 권한 확인
  useEffect(() => {
    if (user.userType !== 'admin') {
      onBack();
    }
  }, [user.userType, onBack]);

  // 통계 로드
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // 공지사항 목록 로드
  useEffect(() => {
    loadAnnouncements();
  }, []);

  // 약관 및 개인정보처리방침 로드
  useEffect(() => {
    loadTermsPrivacy();
  }, []);

  // 이벤트 코드 목록 로드
  useEffect(() => {
    loadEventCodes();
  }, [loadEventCodes]);

  useEffect(() => {
    loadGroupCouponBatches();
  }, [loadGroupCouponBatches]);

  // 서버(Firestore) 청구 설정과 UI·localStorage 동기화 (다른 기기/이용권 페이지와 동일 값 유지)
  useEffect(() => {
    const unsub = onSnapshot(
      billingSettingsDocRef,
      (snap) => {
        if (!snap.exists()) return;
        const d = snap.data() as { paidPaymentEnabled?: unknown; paymentMode?: unknown };
        if (typeof d.paidPaymentEnabled === 'boolean') {
          setPaidPaymentEnabled(d.paidPaymentEnabled);
          localStorage.setItem('paidPaymentEnabled', d.paidPaymentEnabled ? 'true' : 'false');
        }
        if (d.paymentMode === 'test' || d.paymentMode === 'production') {
          setPaymentMode(d.paymentMode);
          localStorage.setItem('paymentMode', d.paymentMode);
        }
      },
      (err) => console.error('청구 설정 구독 오류:', err)
    );
    return unsub;
  }, []);

  // 약관 에디터 내용 동기화 (데이터 로드 시에만 업데이트)
  useEffect(() => {
    if (termsEditorRef.current && termsPrivacy && !loadingTermsPrivacy) {
      const currentContent = termsEditorRef.current.innerHTML;
      const loadedContent = termsPrivacy.termsOfUse.content || '';
      // 에디터가 포커스를 받지 않았을 때만 업데이트 (사용자가 편집 중이 아닐 때)
      if (document.activeElement !== termsEditorRef.current && currentContent !== loadedContent) {
        termsEditorRef.current.innerHTML = loadedContent;
      }
    } else if (termsEditorRef.current && !termsPrivacy && !loadingTermsPrivacy) {
      // 에디터가 포커스를 받지 않았을 때만 초기화
      if (document.activeElement !== termsEditorRef.current) {
        termsEditorRef.current.innerHTML = '';
      }
    }
  }, [termsPrivacy, loadingTermsPrivacy]);

  // 개인정보처리방침 에디터 내용 동기화 (데이터 로드 시에만 업데이트)
  useEffect(() => {
    if (privacyEditorRef.current && termsPrivacy && !loadingTermsPrivacy) {
      const currentContent = privacyEditorRef.current.innerHTML;
      const loadedContent = termsPrivacy.privacyPolicy.content || '';
      // 에디터가 포커스를 받지 않았을 때만 업데이트 (사용자가 편집 중이 아닐 때)
      if (document.activeElement !== privacyEditorRef.current && currentContent !== loadedContent) {
        privacyEditorRef.current.innerHTML = loadedContent;
      }
    } else if (privacyEditorRef.current && !termsPrivacy && !loadingTermsPrivacy) {
      // 에디터가 포커스를 받지 않았을 때만 초기화
      if (document.activeElement !== privacyEditorRef.current) {
        privacyEditorRef.current.innerHTML = '';
      }
    }
  }, [termsPrivacy, loadingTermsPrivacy]);

  // 모달이 열릴 때 에디터 내용 설정
  useEffect(() => {
    if (showTermsModal && modalEditorRef.current) {
      // 모달이 열린 후 에디터에 내용 설정
      setTimeout(() => {
        if (modalEditorRef.current) {
          modalEditorRef.current.innerHTML = modalContent || '';
          if (!isTermsModalReadOnly) {
          modalEditorRef.current.focus();
          }
        }
      }, 100);
    }
  }, [showTermsModal, modalContent, isTermsModalReadOnly]);

  // 공지사항 모달이 열릴 때 에디터에 내용 설정
  useEffect(() => {
    if (showAnnouncementModal && announcementModalEditorRef.current) {
      setTimeout(() => {
        if (announcementModalEditorRef.current) {
          if (editingAnnouncement) {
            announcementModalEditorRef.current.innerHTML = editingAnnouncement.content;
          } else {
            announcementModalEditorRef.current.innerHTML = '';
          }
          announcementModalEditorRef.current.focus();
        }
      }, 100);
    }
  }, [showAnnouncementModal, editingAnnouncement]);



  // 공지사항 목록 로드
  const loadAnnouncements = async () => {
    setLoadingAnnouncements(true);
    try {
      const data = await announcementService.getAnnouncements();
      setAnnouncements(data);
    } catch (error) {
      console.error('공지사항 목록 로드 실패:', error);
    } finally {
      setLoadingAnnouncements(false);
    }
  };


  // 공지사항 목록 핸들러들
  const handleOpenAnnouncementModal = (announcementToEdit: Announcement | null = null) => {
    if (announcementToEdit) {
      setEditingAnnouncement(announcementToEdit);
      setAnnouncementModalTitle(announcementToEdit.title || '');
      setAnnouncementModalContent(announcementToEdit.content);
    } else {
      setEditingAnnouncement(null);
      setAnnouncementModalTitle('');
      setAnnouncementModalContent('');
    }
    setShowAnnouncementModal(true);
  };

  const handleCloseAnnouncementModal = () => {
    setShowAnnouncementModal(false);
    setEditingAnnouncement(null);
    setAnnouncementModalTitle('');
    setAnnouncementModalContent('');
  };

  const handleSaveAnnouncementModal = async () => {
    if (!announcementModalTitle.trim()) {
      alert('공지사항 제목을 입력해주세요.');
      return;
    }
    const textContent = (announcementModalContent || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    if (!textContent) {
      alert('공지사항 내용을 입력해주세요.');
      return;
    }
    try {
      if (editingAnnouncement) {
        // 수정
        await announcementService.updateAnnouncement(
          editingAnnouncement.id,
          announcementModalContent,
          announcementModalTitle || undefined
        );
        alert('공지사항이 수정되었습니다.');
      } else {
        // 생성
        await announcementService.createAnnouncement(
          announcementModalContent,
          announcementModalTitle || undefined
        );
        alert('공지사항이 생성되었습니다.');
      }

      await loadAnnouncements();
      handleCloseAnnouncementModal();
    } catch (error: any) {
      console.error('공지사항 저장 실패:', error);
      alert(`공지사항 ${editingAnnouncement ? '수정' : '생성'}에 실패했습니다: ${error?.message || '알 수 없는 오류'}`);
    } finally {
      // 공지사항 저장 완료
    }
  };

  const handleDeleteAnnouncement = async (announcementId: string) => {
    if (!window.confirm('정말로 이 공지사항을 삭제하시겠습니까?')) {
      return;
    }

    try {
      await announcementService.deleteAnnouncement(announcementId);
      await loadAnnouncements();
      alert('공지사항이 삭제되었습니다.');
    } catch (error: any) {
      console.error('공지사항 삭제 실패:', error);
      alert(`공지사항 삭제에 실패했습니다: ${error?.message || '알 수 없는 오류'}`);
    }
  };

  // 약관 및 개인정보처리방침 로드
  const loadTermsPrivacy = async () => {
    setLoadingTermsPrivacy(true);
    try {
      const historyData = await termsPrivacyService.getTermsPrivacyWithHistory();
      if (historyData) {
        setTermsPrivacyHistory(historyData);
        // 최신 버전만 표시용
        const latestTerms = historyData.termsOfUse[0] || null;
        const latestPrivacy = historyData.privacyPolicy[0] || null;
        
        setTermsPrivacy({
          termsOfUse: latestTerms || {
            content: '',
            createdAt: new Date(),
            updatedAt: new Date()
          },
          privacyPolicy: latestPrivacy || {
            content: '',
            createdAt: new Date(),
            updatedAt: new Date()
          }
        });
        setTermsContent(latestTerms?.content || '');
        setPrivacyContent(latestPrivacy?.content || '');
      } else {
        setTermsPrivacyHistory({ termsOfUse: [], privacyPolicy: [] });
        setTermsPrivacy(null);
        setTermsContent('');
        setPrivacyContent('');
      }
    } catch (error) {
      console.error('약관 및 개인정보처리방침 로드 실패:', error);
    } finally {
      setLoadingTermsPrivacy(false);
    }
  };


  // 공지사항 클릭 시 모달 열기
  const handleAnnouncementClick = (index: number) => {
    setSelectedAnnouncementIndex(index);
  };

  // 공지사항 모달 닫기
  const handleCloseAnnouncementViewModal = () => {
    setSelectedAnnouncementIndex(null);
  };



  // 모달 에디터 명령 실행
  const execModalCommand = (command: string, value?: string) => {
    if (modalEditorRef.current) {
      modalEditorRef.current.focus();
      document.execCommand(command, false, value);
      const content = modalEditorRef.current.innerHTML;
      setModalContent(content);
    }
  };

  // 약관/개인정보처리방침 편집 모달 열기
  const handleOpenTermsModal = (
    type: 'terms' | 'privacy',
    isEdit: boolean = true,
    contentOverride?: string,
    readOnly: boolean = false
  ) => {
    setEditingTermsType(type);
    setIsTermsModalReadOnly(readOnly);
    
    if (contentOverride !== undefined) {
      setModalContent(contentOverride || '');
    } else if (isEdit && termsPrivacy) {
      const content = type === 'terms' ? termsPrivacy.termsOfUse.content : termsPrivacy.privacyPolicy.content;
      setModalContent(content);
    } else {
      setModalContent('');
    }
    
    setShowTermsModal(true);
  };

  // 모달 닫기
  const handleCloseTermsModal = () => {
    setShowTermsModal(false);
    setModalContent('');
    setIsTermsModalReadOnly(false);
  };

  // 모달 에디터 변경 핸들러
  const handleModalEditorChange = () => {
    if (modalEditorRef.current) {
      const content = modalEditorRef.current.innerHTML;
      setModalContent(content);
    }
  };

  const handleAnnouncementModalEditorChange = () => {
    if (announcementModalEditorRef.current) {
      const content = announcementModalEditorRef.current.innerHTML;
      setAnnouncementModalContent(content);
    }
  };

  // 공지사항 편집 모달용 폰트 크기 변경 함수들
  const handleAnnouncementModalFontSizeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 8 && value <= 400) {
      if (!announcementModalEditorRef.current) return;

      announcementModalEditorRef.current.focus();

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        // 현재 선택된 요소 또는 그 부모 요소에 폰트 크기 적용
        let element: Node | null = range.commonAncestorContainer;

        while (element && element !== announcementModalEditorRef.current) {
          if (element.nodeType === 1) {
            const htmlElement = element as HTMLElement;
            const blockTags = ['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
            if (blockTags.includes(htmlElement.tagName)) {
              htmlElement.style.fontSize = `${value}pt`;
              break;
            }
          }
          element = element.parentElement || element.parentNode;
        }

        setAnnouncementModalFontSize(value);
        const content = announcementModalEditorRef.current.innerHTML;
        setAnnouncementModalContent(content);
      }
    }
  };

  const changeAnnouncementModalFontSize = (delta: number) => {
    const newSize = Math.max(8, Math.min(400, announcementModalFontSize + delta));
    if (newSize !== announcementModalFontSize) {
      if (!announcementModalEditorRef.current) return;

      announcementModalEditorRef.current.focus();

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        // 현재 선택된 요소 또는 그 부모 요소에 폰트 크기 적용
        let element: Node | null = range.commonAncestorContainer;

        while (element && element !== announcementModalEditorRef.current) {
          if (element.nodeType === 1) {
            const htmlElement = element as HTMLElement;
            const blockTags = ['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
            if (blockTags.includes(htmlElement.tagName)) {
              htmlElement.style.fontSize = `${newSize}pt`;
              break;
            }
          }
          element = element.parentElement || element.parentNode;
        }

        setAnnouncementModalFontSize(newSize);
        const content = announcementModalEditorRef.current.innerHTML;
        setAnnouncementModalContent(content);
      }
    }
  };

  const handleAnnouncementModalColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    execAnnouncementModalCommand('foreColor', e.target.value);
  };

  const handleAnnouncementModalImageButtonClick = () => {
    announcementModalImageInputRef.current?.click();
  };

  const handleAnnouncementModalImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingAnnouncementModalImage(true);

    try {
      const webpBlob = await fileToWebpBlob(file, 1920, 0.8);
      const firebaseStorageRef = ref(
        storage,
        `announcements/${Date.now()}.${IMAGE_WEBP_EXT}`
      );
      const uploadTask = uploadBytes(firebaseStorageRef, webpBlob, { contentType: IMAGE_WEBP_MIME });
      const snapshot = await uploadTask;
      const downloadURL = await getDownloadURL(snapshot.ref);

      // 에디터에 이미지 삽입
      execAnnouncementModalCommand('insertImage', downloadURL);

      const content = announcementModalEditorRef.current?.innerHTML || '';
      setAnnouncementModalContent(content);

    } catch (error) {
      console.error('이미지 업로드 실패:', error);
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      setIsUploadingAnnouncementModalImage(false);
      if (announcementModalImageInputRef.current) {
        announcementModalImageInputRef.current.value = '';
      }
    }
  };

  // 공지사항 편집 모달용 링크 클릭 시 수정 모달 열기
  const handleAnnouncementModalEditorClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const link = target.closest('a[data-announcement-link="button"]');
    if (link) {
      e.preventDefault();
      e.stopPropagation();
      const anchor = link as HTMLAnchorElement;
      announcementModalEditingLinkRef.current = anchor;
      setAnnouncementModalLinkText(anchor.textContent || '');
      setAnnouncementModalLinkUrl(anchor.href || '');
      setIsAnnouncementModalLinkModalOpen(true);
    }
  };

  // 공지사항 편집 모달용 링크 삽입/수정 함수
  const handleAnnouncementModalInsertLink = () => {
    if (!announcementModalLinkText.trim() || !announcementModalLinkUrl.trim()) {
      alert('링크 텍스트와 URL을 모두 입력해주세요.');
      return;
    }

    if (!announcementModalEditorRef.current) return;

    const editingLink = announcementModalEditingLinkRef.current;
    if (editingLink) {
      // 기존 링크 수정
      const url = announcementModalLinkUrl.startsWith('http://') || announcementModalLinkUrl.startsWith('https://')
        ? announcementModalLinkUrl
        : `https://${announcementModalLinkUrl}`;
      editingLink.href = url;
      editingLink.textContent = announcementModalLinkText;
      announcementModalEditingLinkRef.current = null;
      setAnnouncementModalContent(announcementModalEditorRef.current.innerHTML);
      setIsAnnouncementModalLinkModalOpen(false);
      setAnnouncementModalLinkText('');
      setAnnouncementModalLinkUrl('');
      return;
    }

    announcementModalEditorRef.current.focus();

    const selection = window.getSelection();
    if (!selection) return;

    // 저장된 커서 위치 사용 (링크 모달 열 때 저장) 또는 현재 선택 영역
    let range: Range | null = null;
    const saved = announcementModalSavedSelectionRef.current;
    const editor = announcementModalEditorRef.current;
    if (saved && editor.contains(saved.startContainer)) {
      range = saved;
      selection.removeAllRanges();
      selection.addRange(range);
    } else if (selection.rangeCount > 0 && editor.contains(selection.anchorNode)) {
      range = selection.getRangeAt(0);
    }
    announcementModalSavedSelectionRef.current = null;

    if (range) {
      // 링크 요소 생성 — 배경/텍스트 색은 저장하지 않고 표시 시 라이트/다크 모드에 따라 적용
      const linkElement = document.createElement('a');
      linkElement.href = announcementModalLinkUrl.startsWith('http://') || announcementModalLinkUrl.startsWith('https://')
        ? announcementModalLinkUrl
        : `https://${announcementModalLinkUrl}`;
      linkElement.textContent = announcementModalLinkText;
      linkElement.target = '_blank';
      linkElement.rel = 'noopener noreferrer';
      linkElement.setAttribute('data-announcement-link', 'button');

      // 구조 스타일 + 편집 시 모드별 텍스트 색상 (표시 시 AnnouncementModal CSS에서 덮어씀)
      linkElement.style.display = 'inline-block';
      linkElement.style.padding = '12px 24px';
      linkElement.style.borderRadius = '10px';
      linkElement.style.border = '2px solid var(--color-border)';
      linkElement.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
      linkElement.style.color = 'var(--color-text-primary)';
      linkElement.style.fontSize = '15px';
      linkElement.style.fontWeight = '500';
      linkElement.style.cursor = 'pointer';
      linkElement.style.textDecoration = 'none';
      linkElement.style.margin = '4px';

      // 선택된 텍스트가 있으면 대체, 없으면 삽입
      if (!range.collapsed) {
        range.deleteContents();
      }
      range.insertNode(linkElement);

      // 커서를 링크 뒤로 이동
      const newRange = document.createRange();
      newRange.setStartAfter(linkElement);
      newRange.setEndAfter(linkElement);
      selection.removeAllRanges();
      selection.addRange(newRange);

      const content = announcementModalEditorRef.current.innerHTML;
      setAnnouncementModalContent(content);

      // 모달 닫기
      setIsAnnouncementModalLinkModalOpen(false);
      setAnnouncementModalLinkText('');
      setAnnouncementModalLinkUrl('');
    } else {
      // 커서 위치 없으면 에디터 끝에 삽입
      const editor = announcementModalEditorRef.current;
      const fallbackRange = document.createRange();
      fallbackRange.selectNodeContents(editor);
      fallbackRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(fallbackRange);
      const linkElement = document.createElement('a');
      linkElement.href = announcementModalLinkUrl.startsWith('http://') || announcementModalLinkUrl.startsWith('https://')
        ? announcementModalLinkUrl
        : `https://${announcementModalLinkUrl}`;
      linkElement.textContent = announcementModalLinkText;
      linkElement.target = '_blank';
      linkElement.rel = 'noopener noreferrer';
      linkElement.setAttribute('data-announcement-link', 'button');
      linkElement.style.display = 'inline-block';
      linkElement.style.padding = '12px 24px';
      linkElement.style.borderRadius = '10px';
      linkElement.style.border = '2px solid var(--color-border)';
      linkElement.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
      linkElement.style.color = 'var(--color-text-primary)';
      linkElement.style.fontSize = '15px';
      linkElement.style.fontWeight = '500';
      linkElement.style.cursor = 'pointer';
      linkElement.style.textDecoration = 'none';
      linkElement.style.margin = '4px';
      fallbackRange.insertNode(linkElement);
      const newRange = document.createRange();
      newRange.setStartAfter(linkElement);
      newRange.setEndAfter(linkElement);
      selection.removeAllRanges();
      selection.addRange(newRange);
      setAnnouncementModalContent(editor.innerHTML);
      setIsAnnouncementModalLinkModalOpen(false);
      setAnnouncementModalLinkText('');
      setAnnouncementModalLinkUrl('');
    }
  };

  const execAnnouncementModalCommand = (command: string, value?: string) => {
    if (announcementModalEditorRef.current) {
      announcementModalEditorRef.current.focus();
      document.execCommand(command, false, value);
      const content = announcementModalEditorRef.current.innerHTML;
      setAnnouncementModalContent(content);
    }
  };

  // 모달에서 저장
  const handleSaveModalContent = async () => {
    setSavingTermsPrivacy(true);
    try {
      const content = modalEditorRef.current?.innerHTML?.trim() || '';
      console.log('💾 저장 시작:', {
        type: editingTermsType,
        contentLength: content.length,
        hasContent: !!content,
        contentPreview: content.substring(0, 100) + (content.length > 100 ? '...' : '')
      });

      // 내용이 비어있으면 저장하지 않음
      if (!content) {
        alert('저장할 내용이 없습니다.');
        return;
      }

      if (editingTermsType === 'terms') {
        await termsPrivacyService.saveTermsOfUse(content);
      } else {
        await termsPrivacyService.savePrivacyPolicy(content);
      }

      console.log('✅ 저장 성공');
      await loadTermsPrivacy();
      handleCloseTermsModal();
      alert(editingTermsType === 'terms' ? '서비스 이용약관이 저장되었습니다.' : '개인정보처리방침이 저장되었습니다.');
    } catch (error: any) {
      console.error('❌ 저장 실패:', error);
      console.error('에러 상세:', {
        code: error?.code,
        message: error?.message,
        stack: error?.stack
      });
      alert(`저장에 실패했습니다: ${error?.message || '알 수 없는 오류'}`);
    } finally {
      setSavingTermsPrivacy(false);
    }
  };

  // 날짜 포맷팅
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 삭제 확인 모달 열기
  const handleOpenDeleteConfirm = (type: 'terms' | 'privacy', itemId: string) => {
    setItemToDelete({ type, id: itemId });
    setShowDeleteConfirmModal(true);
  };

  // 삭제 확인 모달 닫기
  const handleCloseDeleteConfirm = () => {
    setShowDeleteConfirmModal(false);
    setItemToDelete(null);
  };

  // 항목 삭제 실행
  const handleDeleteItem = async () => {
    if (!itemToDelete) return;

    setDeletingItem(true);
    try {
      if (itemToDelete.type === 'terms') {
        await termsPrivacyService.deleteTermsOfUseItem(itemToDelete.id);
      } else {
        await termsPrivacyService.deletePrivacyPolicyItem(itemToDelete.id);
      }
      await loadTermsPrivacy();
      handleCloseDeleteConfirm();
      alert('삭제되었습니다.');
    } catch (error: any) {
      console.error('삭제 실패:', error);
      alert(`삭제에 실패했습니다: ${error?.message || '알 수 없는 오류'}`);
    } finally {
      setDeletingItem(false);
    }
  };










  const handleRecomputeUserStats = async () => {
    setRecomputingStats(true);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        alert('로그인이 필요합니다.');
        return;
      }
      const url = 'https://asia-northeast3-luckytem-class-lab-00.cloudfunctions.net/recomputeUserStatsHttp';
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: '{}',
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success) {
        clearAdminStatsListLocalCache();
        await loadStats();
      } else {
        alert(data?.error ?? `집계 다시 계산에 실패했습니다. (${res.status})`);
      }
    } catch (e: any) {
      console.error('recomputeUserStats 오류', e);
      alert(e?.message ?? '집계 다시 계산 중 오류가 발생했습니다.');
    } finally {
      setRecomputingStats(false);
    }
  };

  const handleGenerateEventCode = async () => {
    const duration = parseInt(eventCodeDuration, 10);
    const maxUsers = parseInt(eventCodeMaxUsers, 10);

    if (!Number.isFinite(duration) || duration <= 0) {
      setEventCodeError('이용기간을 1개월 이상으로 입력해주세요.');
      return;
    }
    if (!Number.isFinite(maxUsers) || maxUsers <= 0) {
      setEventCodeError('활성 사용자 수를 1명 이상으로 입력해주세요.');
      return;
    }

    setEventCodeGenerating(true);
    setEventCodeError('');

    try {
      const result = await upgradeService.createEventCode(user.id, {
        targetUserType: eventCodePlan,
        durationMonths: duration,
        maxActivations: maxUsers,
        name: eventCodeName.trim() || undefined
      });

      if (result.success && result.code) {
        setEventCodeValue(result.code);
        await loadEventCodes();
      } else {
        setEventCodeError(result.error || '이벤트 코드 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('이벤트 코드 생성 오류:', error);
      setEventCodeError('이벤트 코드 생성 중 오류가 발생했습니다.');
    } finally {
      setEventCodeGenerating(false);
    }
  };

  const handleGenerateGroupCouponByAdmin = async () => {
    const duration = parseInt(groupIssueDuration, 10);
    const quantity = parseInt(groupIssueQuantity, 10);
    const targetEmail = groupIssueEmail.trim();
    const groupName = groupIssueName.trim();

    if (!groupName) {
      setGroupIssueError('그룹명을 입력해주세요.');
      return;
    }
    if (!targetEmail || !targetEmail.includes('@')) {
      setGroupIssueError('이메일 주소를 올바르게 입력해주세요.');
      return;
    }
    if (!Number.isFinite(duration) || duration <= 0 || duration > 12) {
      setGroupIssueError('이용기간은 1~12개월 사이로 입력해주세요.');
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setGroupIssueError('활성 사용자 수를 1명 이상 입력해주세요.');
      return;
    }

    setGroupIssueGenerating(true);
    setGroupIssueError('');
    setGroupIssueBatchId('');
    setGroupIssueCode('');

    try {
      const plans = await licenseService.getPlans();
      const planDuration = duration === 12 ? 12 : 1;
      const plan = plans.find((p) => p.planType === groupIssuePlan && p.duration === planDuration);
      if (!plan) {
        setGroupIssueError('선택한 플랜 정보를 찾을 수 없습니다.');
        return;
      }

      // 수동 결제/입금 건 발급은 PG 결제 없이 코드 발급 가능한 테스트모드 callable을 사용
      const issued = await licenseService.issueGroupCouponsTestMode(plan.id, {
        durationMonths: duration,
        quantity,
        groupName,
        targetEmail,
      });

      if (issued.success) {
        setGroupIssueBatchId(issued.batchId || '');
        setGroupIssueCode(issued.groupCode || '');
        await loadGroupCouponBatches();
      } else {
        setGroupIssueError(issued.error || '그룹 이용권 발급에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('관리자 그룹 이용권 발급 오류:', error);
      setGroupIssueError(error?.message || '그룹 이용권 발급 중 오류가 발생했습니다.');
    } finally {
      setGroupIssueGenerating(false);
    }
  };

  const handleToggleEventCode = async (code: string, isClosed: boolean) => {
    if (eventCodeToggling.has(code)) return;
    setEventCodeToggling(prev => new Set(prev).add(code));
    try {
      const result = await upgradeService.setEventCodeClosed(user.id, code, isClosed);
      if (result.success) {
        await loadEventCodes();
      } else {
        alert(result.error || '이벤트 코드 상태 변경에 실패했습니다.');
      }
    } catch (error) {
      console.error('이벤트 코드 상태 변경 오류:', error);
      alert('이벤트 코드 상태 변경 중 오류가 발생했습니다.');
    } finally {
      setEventCodeToggling(prev => {
        const next = new Set(prev);
        next.delete(code);
        return next;
      });
    }
  };

  const handleDeleteEventCode = async (code: string) => {
    if (eventCodeDeleting.has(code)) return;
    if (!window.confirm('해당 이벤트 코드를 삭제할까요?')) return;

    setEventCodeDeleting(prev => new Set(prev).add(code));
    try {
      const result = await upgradeService.deleteEventCode(user.id, code);
      if (result.success) {
        await loadEventCodes();
      } else {
        alert(result.error || '이벤트 코드 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('이벤트 코드 삭제 오류:', error);
      alert('이벤트 코드 삭제 중 오류가 발생했습니다.');
    } finally {
      setEventCodeDeleting(prev => {
        const next = new Set(prev);
        next.delete(code);
        return next;
      });
    }
  };

  const handleRefundGroupCouponBatch = async (batchId: string) => {
    if (groupCouponRefunding.has(batchId)) return;
    if (!window.confirm('해당 그룹 이용권 구매 건을 환불할까요?\n(구매 후 7일 이내, 미사용 100% + 활성화 부분환불 합산)')) return;
    setGroupCouponRefunding(prev => new Set(prev).add(batchId));
    try {
      const result = await upgradeService.refundGroupCouponBatch(user.id, batchId);
      if (result.success) {
        alert(
          `환불 처리 완료: ${Number(result.refundKrw || 0).toLocaleString()}원\n(미사용: ${Number(result.unusedRefundKrw || 0).toLocaleString()}원, 활성화: ${Number(result.activatedRefundKrw || 0).toLocaleString()}원)`
        );
        await loadGroupCouponBatches();
      } else {
        alert(result.error || '그룹 이용권 환불에 실패했습니다.');
      }
    } catch (error) {
      console.error('그룹 이용권 환불 오류:', error);
      alert('그룹 이용권 환불 중 오류가 발생했습니다.');
    } finally {
      setGroupCouponRefunding(prev => {
        const next = new Set(prev);
        next.delete(batchId);
        return next;
      });
    }
  };

  const handleOpenResendModal = (batch: { batchId: string; targetEmail?: string }) => {
    const email = batch.targetEmail || '';
    setResendModalBatch({ batchId: batch.batchId, targetEmail: email });
    setResendModalEmail(email);
  };

  const handleCloseResendModal = () => {
    setResendModalBatch(null);
    setResendModalEmail('');
  };

  const handleResendGroupCouponBatch = async (batchId: string, targetEmail: string) => {
    if (groupCouponResending.has(batchId)) return;
    const trimmedEmail = targetEmail.trim();
    if (!trimmedEmail) {
      alert('이메일 주소를 입력해주세요.');
      return;
    }
    setGroupCouponResending(prev => new Set(prev).add(batchId));
    try {
      const result = await upgradeService.resendGroupCouponBatchEmail(batchId, trimmedEmail);
      if (result.success) {
        alert('그룹 코드 재발송이 완료되었습니다.');
        handleCloseResendModal();
        await loadGroupCouponBatches();
      } else {
        alert(result.error || '그룹 코드 재발송에 실패했습니다.');
      }
    } catch (error) {
      console.error('그룹 코드 재발송 오류:', error);
      alert('그룹 코드 재발송 중 오류가 발생했습니다.');
    } finally {
      setGroupCouponResending(prev => {
        const next = new Set(prev);
        next.delete(batchId);
        return next;
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('코드가 클립보드에 복사되었습니다!');
    }).catch(() => {
      alert('복사에 실패했습니다. 수동으로 복사해주세요.');
    });
  };

  const createTestAccount = async () => {
    if (
      !window.confirm(
        `테스트 계정을 생성하시겠습니까?\n이메일: ${ADMIN_TEST_ACCOUNT_EMAIL}\n비밀번호: ${ADMIN_TEST_ACCOUNT_PASSWORD}\n권한: 프로 플랜`
      )
    ) {
      return;
    }
    setCreateTestAccountMessage(null);
    setCreateTestAccountLoading(true);
    try {
      const callable = httpsCallable<Record<string, never>, { success: boolean; uid?: string; email?: string }>(functions, 'createTestAccount');
      const result = await callable({});
      const data = result.data;
      if (data?.success) {
        setCreateTestAccountMessage(`테스트 계정 생성 완료: ${data.email} (프로 플랜). UID: ${data.uid ?? '-'}`);
      } else {
        setCreateTestAccountMessage('테스트 계정 생성에 실패했습니다.');
      }
    } catch (error: any) {
      setCreateTestAccountMessage(error?.message ?? '테스트 계정 생성 중 오류가 발생했습니다.');
      console.error('테스트 계정 생성 오류:', error);
    } finally {
      setCreateTestAccountLoading(false);
    }
  };

  const resetTestAccountLicenseAndPlan = async () => {
    if (
      !window.confirm(
        `${ADMIN_TEST_ACCOUNT_EMAIL} 계정의 이용권·플랜을 초기화할까요?\n\n· userType → 무료(free)\n· license, licenses, upgradeCode 필드 제거\n\n(로그인 중인 테스트 계정은 새로고침 후 반영됩니다.)`
      )
    ) {
      return;
    }
    setResetTestAccountLicenseMessage(null);
    setResetTestAccountLicenseLoading(true);
    try {
      const callable = httpsCallable<Record<string, never>, { success: boolean; uid?: string; email?: string }>(
        functions,
        'resetTestAccountLicense'
      );
      const result = await callable({});
      const data = result.data;
      if (data?.success) {
        setResetTestAccountLicenseMessage(`초기화 완료: ${data.email ?? ADMIN_TEST_ACCOUNT_EMAIL} → 무료 플랜. UID: ${data.uid ?? '-'}`);
      } else {
        setResetTestAccountLicenseMessage('초기화에 실패했습니다.');
      }
    } catch (error: any) {
      setResetTestAccountLicenseMessage(error?.message ?? '이용권 초기화 중 오류가 발생했습니다.');
      console.error('테스트 계정 이용권 초기화 오류:', error);
    } finally {
      setResetTestAccountLicenseLoading(false);
    }
  };

  const deleteAllAnonymousAuth = async () => {
    if (!window.confirm('Firebase Auth의 익명 사용자 계정을 모두 삭제하시겠습니까? (providerData 없음 전부 삭제, guest_ UID 포함)')) {
      return;
    }
    setDeleteAnonymousMessage(null);
    setDeleteAnonymousLoading(true);
    try {
      const callable = httpsCallable<Record<string, never>, { success: boolean; deletedCount: number }>(functions, 'deleteAllAnonymousAuthUsers');
      const result = await callable({});
      const data = result.data;
      if (data?.success) {
        setDeleteAnonymousMessage(`삭제 완료: ${data.deletedCount}명의 익명 사용자 Auth가 삭제되었습니다.`);
      } else {
        setDeleteAnonymousMessage('삭제 처리 중 오류가 발생했습니다.');
      }
    } catch (error: any) {
      const msg = error?.message || error?.code || '알 수 없는 오류';
      setDeleteAnonymousMessage(`실패: ${msg}`);
      console.error('익명 사용자 삭제 오류:', error);
    } finally {
      setDeleteAnonymousLoading(false);
    }
  };

  const totalEventCodePages = Math.max(1, Math.ceil(eventCodes.length / listItemsPerPage));
  const pagedEventCodes = eventCodes.slice(
    (eventCodeCurrentPage - 1) * listItemsPerPage,
    eventCodeCurrentPage * listItemsPerPage
  );
  const totalGroupCouponPages = Math.max(1, Math.ceil(groupCouponBatches.length / listItemsPerPage));
  const pagedGroupCouponBatches = groupCouponBatches.slice(
    (groupCouponCurrentPage - 1) * listItemsPerPage,
    groupCouponCurrentPage * listItemsPerPage
  );

  const filteredUserStatsMembers = React.useMemo(() => {
    if (!userStatsListModal?.members) return [];
    const q = userStatsListSearch.trim().toLowerCase();
    if (!q) return userStatsListModal.members;
    return userStatsListModal.members.filter((m) => {
      const hay = [m.name, m.email, m.id, m.planTypeLabel, m.planPeriodLabel, m.userType, m.planSegment ?? '']
        .join('\n')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [userStatsListModal, userStatsListSearch]);

  const paidPlanSourceCards = [
    {
      planKey: 'basic',
      title: '베이직',
      items: [
        { segment: 'basicIndividualPaid', label: '개별 유료', count: stats?.userStats.basicIndividualPaid ?? 0 },
        { segment: 'basicGroupCoupon', label: '그룹 유료 쿠폰', count: stats?.userStats.basicGroupCoupon ?? 0 },
        { segment: 'basicEventCoupon', label: '이벤트 쿠폰', count: stats?.userStats.basicEventCoupon ?? 0 },
      ],
    },
    {
      planKey: 'standard',
      title: '스탠다드',
      items: [
        { segment: 'standardIndividualPaid', label: '개별 유료', count: stats?.userStats.standardIndividualPaid ?? 0 },
        { segment: 'standardGroupCoupon', label: '그룹 유료 쿠폰', count: stats?.userStats.standardGroupCoupon ?? 0 },
        { segment: 'standardEventCoupon', label: '이벤트 쿠폰', count: stats?.userStats.standardEventCoupon ?? 0 },
      ],
    },
    {
      planKey: 'pro',
      title: '프로',
      items: [
        { segment: 'proIndividualPaid', label: '개별 유료', count: stats?.userStats.proIndividualPaid ?? 0 },
        { segment: 'proGroupCoupon', label: '그룹 유료 쿠폰', count: stats?.userStats.proGroupCoupon ?? 0 },
        { segment: 'proEventCoupon', label: '이벤트 쿠폰', count: stats?.userStats.proEventCoupon ?? 0 },
      ],
    },
  ] as const;

  if (user.userType !== 'admin') {
    return null;
  }

  return (
    <Container>
      <AdminWrapper>
        <Header>
          <Title>관리자 페이지</Title>
          <BackButton onClick={onBack}>돌아가기</BackButton>
        </Header>

        <AdminTestAccountBanner role="status">
          <AdminTestAccountBannerTitle>테스트 계정 로그인 안내</AdminTestAccountBannerTitle>
          <AdminTestAccountBannerRow>
            <strong>이메일</strong>
            {ADMIN_TEST_ACCOUNT_EMAIL}
          </AdminTestAccountBannerRow>
          <AdminTestAccountBannerRow>
            <strong>비밀번호</strong>
            {ADMIN_TEST_ACCOUNT_PASSWORD}
          </AdminTestAccountBannerRow>
          <AdminTestAccountBannerHint>
            생성 버튼으로 Auth·프로 플랜을 맞출 수 있으며, 비밀번호는 항상 위 값으로 갱신됩니다. 이용권만 지우려면 «테스트 계정 이용권·플랜 초기화»를 사용하세요.
          </AdminTestAccountBannerHint>
        </AdminTestAccountBanner>

        <Grid>
          <AdminGridFullRow>
          {/* 사용자 통계 */}
          <Card>
            <CardTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              사용자 통계
              <button
                type="button"
                onClick={handleRecomputeUserStats}
                disabled={recomputingStats || loadingStats}
                style={{
                  fontSize: '12px',
                  padding: '4px 10px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '6px',
                  background: 'var(--color-surface)',
                  color: 'var(--color-text-secondary)',
                  cursor: recomputingStats || loadingStats ? 'not-allowed' : 'pointer'
                }}
              >
                {recomputingStats ? '계산 중...' : '집계 다시 계산'}
              </button>
            </CardTitle>
            {loadingStats ? (
              <div style={{ color: 'var(--color-text-secondary)' }}>로딩 중...</div>
            ) : stats ? (
              <UserStatsLayout>
                {/* 1행: 전체 */}
                <StatsRow>
                  <ClickableTotalStatItem
                    onClick={() => openUserStatsListModal('total')}
                    aria-label="전체 사용자 목록"
                  >
                    <StatLabel>전체</StatLabel>
                    <StatNumber>{stats.userStats.total}</StatNumber>
                  </ClickableTotalStatItem>
                </StatsRow>
                {/* 2행: 관리자 | 프리 */}
                <StatsRow>
                  <ClickableStatItem onClick={() => openUserStatsListModal('admin')} aria-label="관리자 목록">
                    <UserStatsBlockLabel>관리자</UserStatsBlockLabel>
                    <UserStatsBlockNumber>{stats.userStats.admin}</UserStatsBlockNumber>
                  </ClickableStatItem>
                  <ClickableStatItem onClick={() => openUserStatsListModal('free')} aria-label="프리 목록">
                    <UserStatsBlockLabel>프리</UserStatsBlockLabel>
                    <UserStatsBlockNumber>{stats.userStats.free}</UserStatsBlockNumber>
                  </ClickableStatItem>
                </StatsRow>

                {/* 3행: 베이직 | 스탠다드 | 프로 */}
                <StatsRow>
                  <ClickableStatItem onClick={() => openUserStatsListModal('basic')} aria-label="베이직 목록">
                    <UserStatsBlockLabel>베이직</UserStatsBlockLabel>
                    <UserStatsBlockNumber>{stats.userStats.basic}</UserStatsBlockNumber>
                  </ClickableStatItem>
                  <ClickableStatItem onClick={() => openUserStatsListModal('standard')} aria-label="스탠다드 목록">
                    <UserStatsBlockLabel>스탠다드</UserStatsBlockLabel>
                    <UserStatsBlockNumber>{stats.userStats.standard || 0}</UserStatsBlockNumber>
                  </ClickableStatItem>
                  <ClickableStatItem onClick={() => openUserStatsListModal('pro')} aria-label="프로 목록">
                    <UserStatsBlockLabel>프로</UserStatsBlockLabel>
                    <UserStatsBlockNumber>{stats.userStats.pro}</UserStatsBlockNumber>
                  </ClickableStatItem>
                </StatsRow>

                {/* 4행: 플랜별 개별 유료 / 그룹 유료 쿠폰 / 이벤트 쿠폰 */}
                <UserStatsPlanSourceGrid>
                  {paidPlanSourceCards.map((card) => (
                    <UserStatsPlanSourceCard key={card.planKey}>
                      <UserStatsPlanSourceTitle>{card.title}</UserStatsPlanSourceTitle>
                      <UserStatsPlanSourceButtons>
                        {card.items.map((item) => (
                          <UserStatsPlanSourceButton
                            key={item.segment}
                            onClick={() => openUserStatsListModal(item.segment)}
                            aria-label={`${card.title} ${item.label} 목록`}
                          >
                            <UserStatsPlanSourceButtonLabel>{item.label}</UserStatsPlanSourceButtonLabel>
                            <UserStatsPlanSourceButtonCount>{item.count}</UserStatsPlanSourceButtonCount>
                          </UserStatsPlanSourceButton>
                        ))}
                      </UserStatsPlanSourceButtons>
                    </UserStatsPlanSourceCard>
                  ))}
                </UserStatsPlanSourceGrid>
              </UserStatsLayout>
            ) : (
              <div style={{ color: 'var(--color-text-secondary)' }}>통계를 불러올 수 없습니다.</div>
            )}
          </Card>
          </AdminGridFullRow>
          <AdminGridCol2>
          {/* 약관 및 개인정보처리방침 관리 */}
          <Card>
            <CardTitle>약관 및 개인정보처리방침 관리</CardTitle>
            {loadingTermsPrivacy ? (
              <div style={{ color: 'var(--color-text-secondary)' }}>로딩 중...</div>
            ) : (
              <>
                <Description>
                  서비스 이용약관과 개인정보처리방침을 관리하세요.
                </Description>
                
                <TabContainer>
                  <TabButton $isActive={activeTermsTab === 'terms'} onClick={() => setActiveTermsTab('terms')}>
                    서비스 이용약관
                  </TabButton>
                  <TabButton $isActive={activeTermsTab === 'privacy'} onClick={() => setActiveTermsTab('privacy')}>
                    개인정보처리방침
                  </TabButton>
                </TabContainer>

                {activeTermsTab === 'terms' ? (
                  <>
                    {termsPrivacyHistory && termsPrivacyHistory.termsOfUse.length > 0 ? (
                      <>
                        {termsPrivacyHistory.termsOfUse[0] && (
                          <TermsBlock onClick={() => handleOpenTermsModal('terms', true)}>
                            <TermsBlockTitle>등록된 서비스 이용약관</TermsBlockTitle>
                            <TermsBlockDate>최종 수정: {formatDate(termsPrivacyHistory.termsOfUse[0].updatedAt)}</TermsBlockDate>
                          </TermsBlock>
                        )}
                        {termsPrivacyHistory.termsOfUse.slice(1).map((item: any) => (
                          <OldTermsBlock
                            key={item.id}
                            onClick={() => handleOpenTermsModal('terms', true, item.content, true)}
                          >
                            <OldTermsBlockTitle>이전 서비스 이용약관</OldTermsBlockTitle>
                            <OldTermsBlockDate>수정일: {formatDate(item.updatedAt)}</OldTermsBlockDate>
                            <TermsBlockActions>
                              <DeleteButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDeleteConfirm('terms', item.id);
                                }}
                              >
                                <TermsDeleteTrashSvg />
                                삭제
                              </DeleteButton>
                            </TermsBlockActions>
                          </OldTermsBlock>
                        ))}
                      </>
                    ) : null}
                    <CreateButton onClick={() => handleOpenTermsModal('terms', false)}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      서비스 이용약관 만들기
                    </CreateButton>
                  </>
                ) : (
                  <>
                    {termsPrivacyHistory && termsPrivacyHistory.privacyPolicy.length > 0 ? (
                      <>
                        {termsPrivacyHistory.privacyPolicy[0] && (
                          <TermsBlock onClick={() => handleOpenTermsModal('privacy', true)}>
                            <TermsBlockTitle>등록된 개인정보처리방침</TermsBlockTitle>
                            <TermsBlockDate>최종 수정: {formatDate(termsPrivacyHistory.privacyPolicy[0].updatedAt)}</TermsBlockDate>
                          </TermsBlock>
                        )}
                        {termsPrivacyHistory.privacyPolicy.slice(1).map((item: any) => (
                          <OldTermsBlock
                            key={item.id}
                            onClick={() => handleOpenTermsModal('privacy', true, item.content, true)}
                          >
                            <OldTermsBlockTitle>이전 개인정보처리방침</OldTermsBlockTitle>
                            <OldTermsBlockDate>수정일: {formatDate(item.updatedAt)}</OldTermsBlockDate>
                            <TermsBlockActions>
                              <DeleteButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDeleteConfirm('privacy', item.id);
                                }}
                              >
                                <TermsDeleteTrashSvg />
                                삭제
                              </DeleteButton>
                            </TermsBlockActions>
                          </OldTermsBlock>
                        ))}
                      </>
                    ) : null}
                    <CreateButton onClick={() => handleOpenTermsModal('privacy', false)}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      개인정보처리방침 만들기
                    </CreateButton>
                  </>
                )}
              </>
            )}
          </Card>

          {/* 공지사항 목록 관리 */}
          <Card>
            <CardTitle>공지사항 목록 관리</CardTitle>
            <div style={{ marginBottom: '16px', width: '100%' }}>
              <AddButton onClick={() => handleOpenAnnouncementModal()} style={{ width: '100%' }}>
                + 공지사항 만들기
              </AddButton>
            </div>

            {loadingAnnouncements ? (
              <div style={{ color: 'var(--color-text-secondary)' }}>로딩 중...</div>
            ) : (
              <>
                {announcements.length === 0 ? (
                  <div style={{ color: 'var(--color-text-secondary)', textAlign: 'center', padding: '40px' }}>
                    생성된 공지사항이 없습니다.
                  </div>
                ) : (
                  <>
                    {announcements
                      .slice((announcementCurrentPage - 1) * announcementsPerPage, announcementCurrentPage * announcementsPerPage)
                      .map((announcement) => (
                        <AnnouncementBlock
                          key={announcement.id}
                          onClick={() => handleAnnouncementClick(
                            (announcementCurrentPage - 1) * announcementsPerPage +
                            announcements.slice((announcementCurrentPage - 1) * announcementsPerPage, announcementCurrentPage * announcementsPerPage).findIndex(a => a.id === announcement.id)
                          )}
                          style={{ cursor: 'pointer' }}
                        >
                          <AnnouncementHeader>
                            <AnnouncementMeta>
                              <AnnouncementDate>
                                {announcement.createdAt.toLocaleDateString('ko-KR')}
                              </AnnouncementDate>
                              {announcement.title && (
                                <AnnouncementTitle>{announcement.title}</AnnouncementTitle>
                              )}
                            </AnnouncementMeta>
                            <AnnouncementActions>
                            <ActionIcon
                              viewBox="0 0 24 24"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenAnnouncementModal(announcement);
                              }}
                            >
                                <path fill="none" d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path fill="none" d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </ActionIcon>
                              <ActionIcon
                                viewBox="0 0 24 24"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteAnnouncement(announcement.id);
                                }}
                              >
                                <path fill="none" d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </ActionIcon>
                            </AnnouncementActions>
                          </AnnouncementHeader>
                        </AnnouncementBlock>
                      ))}

                    {Math.ceil(announcements.length / announcementsPerPage) > 1 && (
                      <PaginationContainer>
                        <PaginationButton
                          onClick={() => setAnnouncementCurrentPage(Math.max(1, announcementCurrentPage - 1))}
                          disabled={announcementCurrentPage === 1}
                        >
                          ‹
                        </PaginationButton>
                        <PaginationInfo>
                          {announcementCurrentPage} / {Math.ceil(announcements.length / announcementsPerPage)}
                        </PaginationInfo>
                        <PaginationButton
                          onClick={() => setAnnouncementCurrentPage(Math.min(Math.ceil(announcements.length / announcementsPerPage), announcementCurrentPage + 1))}
                          disabled={announcementCurrentPage === Math.ceil(announcements.length / announcementsPerPage)}
                        >
                          ›
                        </PaginationButton>
                      </PaginationContainer>
                    )}
                  </>
                )}
              </>
            )}
          </Card>

          {/* 이벤트 코드 생성 */}
          <Card>
            <CardTitle>이벤트 코드 생성 및 발급 관리</CardTitle>
                <Description>
              이벤트 코드를 생성합니다. 활성 수가 모두 차면 해당 코드는 마감됩니다.
                </Description>
            <div style={{ marginBottom: '12px', width: '100%' }}>
              <AdminInputLabel>이벤트 코드 이름</AdminInputLabel>
              <AdminInput
                type="text"
                placeholder="예: 2025년 1월 이벤트"
                value={eventCodeName}
                onChange={(e) => setEventCodeName(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', textAlign: 'center' }}
              />
            </div>
            <div style={{ marginBottom: '12px', width: '100%', visibility: 'hidden', pointerEvents: 'none' }} aria-hidden="true">
              <AdminInputLabel>이메일 입력</AdminInputLabel>
              <AdminInput type="email" placeholder="example@email.com" readOnly value="" style={{ width: '100%', boxSizing: 'border-box', textAlign: 'center' }} />
            </div>
            <AdminInputRow style={{ gap: '12px', alignItems: 'flex-end', width: '100%' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <AdminInputLabel style={{ textAlign: 'center' }}>이용기간 (개월)</AdminInputLabel>
                <AdminInput
                  type="number"
                  min="1"
                  value={eventCodeDuration}
                  onChange={(e) => setEventCodeDuration(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', textAlign: 'center' }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <AdminInputLabel style={{ textAlign: 'center' }}>활성 사용자 수</AdminInputLabel>
                <AdminInput
                  type="number"
                  min="1"
                  value={eventCodeMaxUsers}
                  onChange={(e) => setEventCodeMaxUsers(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', textAlign: 'center' }}
                />
              </div>
            </AdminInputRow>

            <AdminInputLabel style={{ marginTop: '12px' }}>플랜 선택</AdminInputLabel>
            <EventCodePlanButtonGroup>
              <EventCodePlanButton
                $isActive={eventCodePlan === 'basic'}
                onClick={() => setEventCodePlan('basic')}
              >
                베이직
              </EventCodePlanButton>
              <EventCodePlanButton
                $isActive={eventCodePlan === 'standard'}
                onClick={() => setEventCodePlan('standard')}
              >
                스탠다드
              </EventCodePlanButton>
              <EventCodePlanButton
                $isActive={eventCodePlan === 'pro'}
                onClick={() => setEventCodePlan('pro')}
              >
                프로
              </EventCodePlanButton>
            </EventCodePlanButtonGroup>

            <AddButton
              onClick={handleGenerateEventCode}
              disabled={eventCodeGenerating}
              style={{ marginTop: '12px', width: '100%' }}
            >
              {eventCodeGenerating ? '생성 중...' : '이벤트 코드 생성'}
            </AddButton>

            {eventCodeValue ? (
              <GeneratedCodeDisplay style={{ marginTop: '12px' }}>
                <CodeText>{eventCodeValue}</CodeText>
                <CodeActionButtons>
                  <CopyButton onClick={() => copyToClipboard(eventCodeValue)}>
                    복사
                  </CopyButton>
                </CodeActionButtons>
              </GeneratedCodeDisplay>
            ) : (
              <EmptyCodeDisplay style={{ marginTop: '12px' }}>
                이벤트 코드가 여기에 표시됩니다 (EVT-...)
              </EmptyCodeDisplay>
            )}

            {eventCodeError && (
              <ErrorMessage style={{ marginTop: '8px' }}>
                {eventCodeError}
              </ErrorMessage>
            )}

            <AddButton
              onClick={() => {
                setEventCodeCurrentPage(1);
                setShowEventCodeListModal(true);
              }}
              style={{ marginTop: '12px', width: '100%' }}
            >
              이벤트 코드 목록
            </AddButton>
          </Card>

          {/* 그룹 이용권 발급 관리 */}
          <Card>
            <CardTitle>그룹 이용권 생성 및 발급 관리</CardTitle>
            <Description>
              관리자가 수동 결제건의 그룹 이용권 코드를 생성/발급할 수 있습니다. (비결제 연동 발급)
            </Description>
            <div style={{ marginBottom: '12px', width: '100%' }}>
              <AdminInputLabel>그룹명 입력</AdminInputLabel>
              <AdminInput
                type="text"
                placeholder="예: 서울 캠퍼스 A반"
                value={groupIssueName}
                onChange={(e) => setGroupIssueName(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', textAlign: 'center' }}
              />
            </div>
            <div style={{ marginBottom: '12px', width: '100%' }}>
              <AdminInputLabel>이메일 입력</AdminInputLabel>
              <AdminInput
                type="email"
                placeholder="example@email.com"
                value={groupIssueEmail}
                onChange={(e) => setGroupIssueEmail(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', textAlign: 'center' }}
              />
            </div>
            <AdminInputRow style={{ gap: '12px', alignItems: 'flex-end', width: '100%' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <AdminInputLabel style={{ textAlign: 'center' }}>이용기간 입력 (개월)</AdminInputLabel>
                <AdminInput
                  type="number"
                  min="1"
                  max="12"
                  value={groupIssueDuration}
                  onChange={(e) => setGroupIssueDuration(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', textAlign: 'center' }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <AdminInputLabel style={{ textAlign: 'center' }}>활성사용자 수 입력</AdminInputLabel>
                <AdminInput
                  type="number"
                  min="1"
                  value={groupIssueQuantity}
                  onChange={(e) => setGroupIssueQuantity(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', textAlign: 'center' }}
                />
              </div>
            </AdminInputRow>
            <AdminInputLabel style={{ marginTop: '12px' }}>플랜 선택</AdminInputLabel>
            <EventCodePlanButtonGroup>
              <EventCodePlanButton $isActive={groupIssuePlan === 'basic'} onClick={() => setGroupIssuePlan('basic')}>
                베이직
              </EventCodePlanButton>
              <EventCodePlanButton $isActive={groupIssuePlan === 'standard'} onClick={() => setGroupIssuePlan('standard')}>
                스탠다드
              </EventCodePlanButton>
              <EventCodePlanButton $isActive={groupIssuePlan === 'pro'} onClick={() => setGroupIssuePlan('pro')}>
                프로
              </EventCodePlanButton>
            </EventCodePlanButtonGroup>
            <AddButton
              onClick={handleGenerateGroupCouponByAdmin}
              disabled={groupIssueGenerating}
              style={{ marginTop: '12px', width: '100%' }}
            >
              {groupIssueGenerating ? '생성 중...' : '그룹 이용권 생성'}
            </AddButton>
            {groupIssueCode ? (
              <GeneratedCodeDisplay style={{ marginTop: '12px' }}>
                <CodeText>{groupIssueCode}</CodeText>
                <CodeActionButtons>
                  <CopyButton onClick={() => copyToClipboard(groupIssueCode)}>
                    복사
                  </CopyButton>
                </CodeActionButtons>
              </GeneratedCodeDisplay>
            ) : (
              <EmptyCodeDisplay style={{ marginTop: '12px' }}>
                그룹 이용권 코드가 여기에 표시됩니다 (GRP-...)
              </EmptyCodeDisplay>
            )}
            {groupIssueBatchId && (
              <div style={{ marginTop: '8px', color: 'var(--color-text-secondary)', fontSize: '12px', textAlign: 'center' }}>
                배치 ID: {groupIssueBatchId}
              </div>
            )}
            {groupIssueError && (
              <ErrorMessage style={{ marginTop: '8px' }}>
                {groupIssueError}
              </ErrorMessage>
            )}
            <AddButton
              onClick={() => {
                setGroupCouponCurrentPage(1);
                setShowGroupCouponListModal(true);
              }}
              style={{ marginTop: '12px', width: '100%' }}
            >
              그룹 이용권 목록
            </AddButton>
          </Card>

          {/* 유료 결제 모드 잠금 / 유료 결제 모드 활성화 (유료 플랜 블록 활성화 여부) */}
          <Card>
            <CardTitle>유료 플랜 결제 활성화</CardTitle>
            <Description>
              이용권 페이지에서 유료 플랜 블록을 활성화할지 선택하세요. 저장 시 서버에 반영되어 로그인한 사용자 이용권 화면에 동기화됩니다.
            </Description>
            <PaymentModeButtonGroup>
              <PaymentModeButton
                $isActive={!paidPaymentEnabled}
                onClick={() => handlePaidPaymentEnabledChange(false)}
              >
                유료 결제 모드 잠금(베타 테스트)
              </PaymentModeButton>
              <PaymentModeButton
                $isActive={paidPaymentEnabled}
                onClick={() => handlePaidPaymentEnabledChange(true)}
              >
                유료 결제 모드 활성화
              </PaymentModeButton>
            </PaymentModeButtonGroup>
            <PaymentModeDescription>
              {!paidPaymentEnabled ? (
                <span>
                  유료 결제 모드 잠금: 유료 플랜 블록이 비활성화되며 &quot;이용중지&quot; 뱃지가 표시됩니다. 베타 테스트 기간 안내 문구가 노출됩니다. 쿠폰 코드로 플랜 권한을 획득할 수 있습니다. (
                  {ADMIN_TEST_ACCOUNT_EMAIL} 계정은 잠금 상태에서도 유료 결제가 가능합니다.)
                </span>
              ) : (
                <span>유료 결제 모드 활성화: 유료 플랜 블록이 활성화되어 결제·업그레이드 등 이용권 구입이 가능합니다.</span>
              )}
            </PaymentModeDescription>
          </Card>

          {/* PG 결제 모드 설정 */}
          <Card>
            <CardTitle>PG 결제 모드 설정</CardTitle>
            <Description>
              플랜 구매 시 결제 처리 방식을 선택하세요. 저장 시 서버에 반영되어 로그인한 사용자 이용권 화면에 동기화됩니다.
            </Description>
            <PaymentModeButtonGroup>
              <PaymentModeButton
                $isActive={paymentMode === 'test'}
                onClick={() => handlePaymentModeChange('test')}
              >
                테스트 모드
              </PaymentModeButton>
              <PaymentModeButton
                $isActive={paymentMode === 'production'}
                onClick={() => handlePaymentModeChange('production')}
              >
                실제 결제 모드
              </PaymentModeButton>
            </PaymentModeButtonGroup>
            <PaymentModeDescription>
              {paymentMode === 'test' ? (
                <span>테스트 모드: 플랜 버튼 클릭 시 비밀번호 입력 모달이 표시되며, 실제 결제 없이 이용권이 활성화됩니다.</span>
              ) : (
                <span>실제 결제 모드: 플랜 버튼 클릭 시 PG 결제 화면으로 이동하여 실제 결제가 진행됩니다.</span>
              )}
            </PaymentModeDescription>
          </Card>

          {/* Firebase Auth: 테스트 계정 생성 / 익명 사용자 삭제 */}
          <Card>
            <CardTitle>Firebase Auth</CardTitle>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', marginBottom: '16px' }}>
              로그인 이메일·비밀번호는 페이지 상단 «테스트 계정 로그인 안내»를 참고하세요. 생성 시 프로 플랜으로 맞추고, 이미 있으면 비밀번호만 갱신합니다.
            </p>
            <InitializeButton
              onClick={createTestAccount}
              disabled={createTestAccountLoading}
              style={{ marginRight: '8px' }}
            >
              {createTestAccountLoading && <LoadingSpinner />}
              테스트 계정 생성
            </InitializeButton>
            <InitializeButton
              onClick={resetTestAccountLicenseAndPlan}
              disabled={resetTestAccountLicenseLoading || createTestAccountLoading}
              style={{ marginRight: '8px' }}
            >
              {resetTestAccountLicenseLoading && <LoadingSpinner />}
              테스트 계정 이용권·플랜 초기화
            </InitializeButton>
            {createTestAccountMessage != null && (
              <div style={{ marginTop: '12px', color: 'var(--color-text-primary)', fontSize: '0.9rem' }}>
                {createTestAccountMessage}
              </div>
            )}
            {resetTestAccountLicenseMessage != null && (
              <div style={{ marginTop: '12px', color: 'var(--color-text-primary)', fontSize: '0.9rem' }}>
                {resetTestAccountLicenseMessage}
              </div>
            )}
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem', marginTop: '20px', marginBottom: '16px' }}>
              익명 사용자(providerData 없음) 계정을 모두 삭제합니다. (테스트용: guest_ UID 포함)
            </p>
            <InitializeButton
              onClick={deleteAllAnonymousAuth}
              disabled={deleteAnonymousLoading}
            >
              {deleteAnonymousLoading && <LoadingSpinner />}
              익명 사용자 Auth 모두 삭제
            </InitializeButton>
            {deleteAnonymousMessage != null && (
              <div style={{ marginTop: '12px', color: 'var(--color-text-primary)', fontSize: '0.9rem' }}>
                {deleteAnonymousMessage}
              </div>
            )}
          </Card>

        </AdminGridCol2>
        </Grid>


      </AdminWrapper>

      {/* 약관/개인정보처리방침 편집 모달 */}
      {showTermsModal && (
        <TermsModalOverlay onClick={handleCloseTermsModal}>
          <TermsModalContent onClick={(e) => e.stopPropagation()}>
            <TermsModalHeader>
              <h3>
                {editingTermsType === 'terms'
                  ? (isTermsModalReadOnly ? '서비스 이용약관 확인' : '서비스 이용약관 편집')
                  : (isTermsModalReadOnly ? '개인정보처리방침 확인' : '개인정보처리방침 편집')}
              </h3>
              <TermsModalCloseButton onClick={handleCloseTermsModal}>
                ×
              </TermsModalCloseButton>
            </TermsModalHeader>
            <TermsModalBody>
              {/* 에디터 툴바 */}
              <EditorToolbar style={isTermsModalReadOnly ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
                <ToolbarButton
                  onClick={() => execModalCommand('bold')}
                  title="굵게 (Ctrl+B)"
                >
                  <strong>B</strong>
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => execModalCommand('italic')}
                  title="기울임 (Ctrl+I)"
                >
                  <em>I</em>
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => execModalCommand('underline')}
                  title="밑줄 (Ctrl+U)"
                >
                  <u>U</u>
                </ToolbarButton>
                <div style={{ width: '1px', background: 'var(--color-border)', margin: '0 4px' }} />
                <ToolbarButton
                  onClick={() => execModalCommand('justifyLeft')}
                  title="왼쪽 정렬"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6h18M3 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => execModalCommand('justifyCenter')}
                  title="가운데 정렬"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => execModalCommand('justifyRight')}
                  title="오른쪽 정렬"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </ToolbarButton>
                <div style={{ width: '1px', background: 'var(--color-border)', margin: '0 4px' }} />
                <ToolbarButton
                  onClick={() => execModalCommand('insertUnorderedList')}
                  title="목록"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => execModalCommand('insertOrderedList')}
                  title="번호 목록"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 6h11M10 12h11M10 18h11M4 6h1v1H4zM4 12h1v1H4zM4 18h1v1H4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ToolbarButton>
              </EditorToolbar>

              {/* 에디터 */}
              <TermsModalEditor
                ref={modalEditorRef}
                contentEditable={!isTermsModalReadOnly}
                data-placeholder={editingTermsType === 'terms' ? '서비스 이용약관 내용을 입력하세요...' : '개인정보처리방침 내용을 입력하세요...'}
                onInput={handleModalEditorChange}
                onBlur={handleModalEditorChange}
              />

              {/* 저장 버튼 */}
              <div style={{ marginTop: '16px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                {isTermsModalReadOnly ? (
                  <SaveButton
                    onClick={handleCloseTermsModal}
                    style={{ background: 'var(--color-surface)', color: 'var(--color-text-primary)', border: '2px solid var(--color-border)' }}
                  >
                    닫기
                  </SaveButton>
                ) : (
                  <>
                <SaveButton
                  onClick={handleCloseTermsModal}
                  disabled={savingTermsPrivacy}
                  style={{ background: 'var(--color-surface)', color: 'var(--color-text-primary)', border: '2px solid var(--color-border)' }}
                >
                  취소
                </SaveButton>
                <SaveButton
                  onClick={handleSaveModalContent}
                  disabled={savingTermsPrivacy}
                >
                  {savingTermsPrivacy ? '저장 중...' : '저장'}
                </SaveButton>
                  </>
                )}
              </div>
            </TermsModalBody>
          </TermsModalContent>
        </TermsModalOverlay>
      )}

      {/* 공지사항 편집 모달 */}
      {showAnnouncementModal && (
        <TermsModalOverlay onClick={handleCloseAnnouncementModal}>
          <TermsModalContent onClick={(e) => e.stopPropagation()}>
            <TermsModalHeader>
              <h3>{editingAnnouncement ? '공지사항 수정' : '공지사항 만들기'}</h3>
            </TermsModalHeader>
            <TermsModalBody>
              {/* 제목 입력 */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--color-text-primary)', textAlign: 'center' }}>
                  제목
                </label>
                <input
                  type="text"
                  value={announcementModalTitle}
                  onChange={(e) => setAnnouncementModalTitle(e.target.value)}
                  placeholder="공지사항 제목을 입력하세요..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid var(--color-border)',
                    borderRadius: '8px',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    textAlign: 'center'
                  }}
                />
              </div>

              {/* 에디터 툴바 */}
              <EditorToolbar>
                <ToolbarButton
                  onClick={() => execAnnouncementModalCommand('bold')}
                  title="굵게 (Ctrl+B)"
                >
                  <strong>B</strong>
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => execAnnouncementModalCommand('italic')}
                  title="기울임 (Ctrl+I)"
                >
                  <em>I</em>
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => execAnnouncementModalCommand('underline')}
                  title="밑줄 (Ctrl+U)"
                >
                  <u>U</u>
                </ToolbarButton>
                <div style={{ width: '1px', background: 'var(--color-border)', margin: '0 4px' }} />
                <ToolbarButton
                  onClick={() => execAnnouncementModalCommand('justifyLeft')}
                  title="왼쪽 정렬"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6h18M3 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => execAnnouncementModalCommand('justifyCenter')}
                  title="가운데 정렬"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6h18M6 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => execAnnouncementModalCommand('justifyRight')}
                  title="오른쪽 정렬"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6h18M9 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </ToolbarButton>
                <div style={{ width: '1px', background: 'var(--color-border)', margin: '0 4px' }} />
                <FontSizeContainer>
                  <FontSizeInput
                    type="number"
                    value={announcementModalFontSize}
                    onChange={handleAnnouncementModalFontSizeInputChange}
                    min={8}
                    max={400}
                    title="폰트 크기 (PT)"
                  />
                  <FontSizeLabel>PT</FontSizeLabel>
                  <FontSizeButton
                    onClick={() => changeAnnouncementModalFontSize(1)}
                    title="폰트 크기 증가"
                  >
                    +
                  </FontSizeButton>
                  <FontSizeButton
                    onClick={() => changeAnnouncementModalFontSize(-1)}
                    title="폰트 크기 감소"
                  >
                    −
                  </FontSizeButton>
                </FontSizeContainer>
                <ToolbarColorInput
                  type="color"
                  onChange={handleAnnouncementModalColorChange}
                  title="텍스트 색상"
                  defaultValue="#000000"
                />
                <div style={{ width: '1px', background: 'var(--color-border)', margin: '0 4px' }} />
                <ToolbarButton
                  onClick={() => execAnnouncementModalCommand('insertUnorderedList')}
                  title="목록"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ToolbarButton>
                <ToolbarButton
                  onClick={() => execAnnouncementModalCommand('insertOrderedList')}
                  title="번호 목록"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 6h11M10 12h11M10 18h11M4 6h1v1H4zM4 12h1v1H4zM4 18h1v1H4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ToolbarButton>
                <div style={{ width: '1px', background: 'var(--color-border)', margin: '0 4px' }} />
                <ToolbarButton
                  onMouseDown={(e) => {
                    // 포커스 이동 전에 커서 위치 저장 (클릭 시 선택 영역이 사라지므로)
                    const sel = window.getSelection();
                    const editor = announcementModalEditorRef.current;
                    if (sel && sel.rangeCount > 0 && editor) {
                      const range = sel.getRangeAt(0);
                      if (editor.contains(range.startContainer)) {
                        announcementModalSavedSelectionRef.current = range.cloneRange();
                      }
                    }
                    e.preventDefault();
                    setIsAnnouncementModalLinkModalOpen(true);
                  }}
                  title="링크 삽입"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ToolbarButton>
                <ToolbarButton
                  onClick={handleAnnouncementModalImageButtonClick}
                  title="이미지 삽입"
                  disabled={isUploadingAnnouncementModalImage}
                >
                  {isUploadingAnnouncementModalImage ? (
                    <div style={{ width: '16px', height: '16px', border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="2"/>
                      <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </ToolbarButton>
                <input
                  ref={announcementModalImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAnnouncementModalImageUpload}
                  style={{ display: 'none' }}
                />
              </EditorToolbar>

              {/* 에디터: dangerouslySetInnerHTML 제거 — 모달 열릴 때 ref로만 초기화하여 타이핑 시 역순 입력 방지 */}
              <TermsModalEditor
                ref={announcementModalEditorRef}
                contentEditable
                suppressContentEditableWarning
                data-placeholder="공지사항 내용을 입력하세요..."
                onInput={handleAnnouncementModalEditorChange}
                onBlur={handleAnnouncementModalEditorChange}
                onClick={handleAnnouncementModalEditorClick}
              />

              {/* 저장 버튼 */}
              <div style={{ marginTop: '16px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <SaveButton
                  onClick={handleCloseAnnouncementModal}
                  style={{ background: 'var(--color-surface)', color: 'var(--color-text-primary)', border: '2px solid var(--color-border)' }}
                >
                  취소
                </SaveButton>
                <SaveButton
                  onClick={handleSaveAnnouncementModal}
                >
                  저장
                </SaveButton>
              </div>
            </TermsModalBody>
          </TermsModalContent>
        </TermsModalOverlay>
      )}

      {/* 공지사항 편집 모달용 링크 삽입 모달 */}
      {isAnnouncementModalLinkModalOpen && (
        <AnnouncementLinkModalOverlay onClick={() => {
          setIsAnnouncementModalLinkModalOpen(false);
          announcementModalEditingLinkRef.current = null;
          setAnnouncementModalLinkText('');
          setAnnouncementModalLinkUrl('');
        }}>
          <LinkModalContent onClick={(e) => e.stopPropagation()}>
            <LinkModalTitle>{announcementModalEditingLinkRef.current ? '링크 수정' : '링크 삽입'}</LinkModalTitle>
            <div>
              <LinkModalLabel>링크 텍스트</LinkModalLabel>
              <LinkModalInput
                type="text"
                value={announcementModalLinkText}
                onChange={(e) => setAnnouncementModalLinkText(e.target.value)}
                placeholder="표시할 텍스트를 입력하세요"
                autoFocus
              />
            </div>
            <div>
              <LinkModalLabel>링크 URL</LinkModalLabel>
              <LinkModalInput
                type="text"
                value={announcementModalLinkUrl}
                onChange={(e) => setAnnouncementModalLinkUrl(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
            <LinkModalButtons>
              <LinkModalButton
                onClick={() => {
                  setIsAnnouncementModalLinkModalOpen(false);
                  announcementModalEditingLinkRef.current = null;
                  setAnnouncementModalLinkText('');
                  setAnnouncementModalLinkUrl('');
                }}
              >
                취소
              </LinkModalButton>
              <LinkModalButton
                $primary
                onClick={handleAnnouncementModalInsertLink}
              >
                확인
              </LinkModalButton>
            </LinkModalButtons>
          </LinkModalContent>
        </AnnouncementLinkModalOverlay>
      )}

      {/* 공지사항 뷰 모달 */}
      {selectedAnnouncementIndex !== null && announcements[selectedAnnouncementIndex] && (
        <AnnouncementViewModal onClick={handleCloseAnnouncementViewModal}>
          <AnnouncementViewContent onClick={(e) => e.stopPropagation()}>
            <AnnouncementViewHeader>
              <AnnouncementViewCloseButton onClick={handleCloseAnnouncementViewModal}>
                ✕
              </AnnouncementViewCloseButton>
              <AnnouncementViewTitle>
                {announcements[selectedAnnouncementIndex].title || '제목 없음'}
              </AnnouncementViewTitle>
              <AnnouncementViewDate style={{ textAlign: 'center', marginTop: '8px', color: 'var(--color-text-secondary)' }}>
                {announcements[selectedAnnouncementIndex].createdAt.toLocaleDateString('ko-KR')}
              </AnnouncementViewDate>
            </AnnouncementViewHeader>
            <AnnouncementViewBody>
              <div dangerouslySetInnerHTML={{ __html: announcements[selectedAnnouncementIndex].content }} />
            </AnnouncementViewBody>
          </AnnouncementViewContent>
        </AnnouncementViewModal>
      )}

      {/* 삭제 확인 모달 */}
      <DeleteConfirmModal $isOpen={showDeleteConfirmModal} onClick={handleCloseDeleteConfirm}>
        <DeleteConfirmContent onClick={(e) => e.stopPropagation()}>
          <DeleteConfirmTitle>삭제 확인</DeleteConfirmTitle>
          <DeleteConfirmMessage>
            정말로 이 {itemToDelete?.type === 'terms' ? '서비스 이용약관' : '개인정보처리방침'}을 삭제하시겠습니까?<br />
            이 작업은 되돌릴 수 없습니다.
          </DeleteConfirmMessage>
          <DeleteConfirmButtons>
            <DeleteConfirmButton
              onClick={handleCloseDeleteConfirm}
              disabled={deletingItem}
            >
              취소
            </DeleteConfirmButton>
            <DeleteConfirmButton
              $danger
              onClick={handleDeleteItem}
              disabled={deletingItem}
            >
              {deletingItem ? '삭제 중...' : '삭제'}
            </DeleteConfirmButton>
          </DeleteConfirmButtons>
        </DeleteConfirmContent>
      </DeleteConfirmModal>

      {/* 이벤트 코드 목록 모달 */}
      {showEventCodeListModal && (
        <ListModalOverlay onClick={() => setShowEventCodeListModal(false)}>
          <ListModalContent onClick={(e) => e.stopPropagation()}>
            <ListModalHeader>
              <ListModalTitle>이벤트 코드 목록 / 마감 현황</ListModalTitle>
              <TermsModalCloseButton onClick={() => setShowEventCodeListModal(false)}>×</TermsModalCloseButton>
            </ListModalHeader>
            <ListModalBody>
              <Description style={{ marginTop: 0 }}>
                이벤트 코드의 활성화 현황을 확인하고 수동으로 마감/재개할 수 있습니다.
              </Description>
              <AdminActionButton onClick={loadEventCodes} disabled={loadingEventCodes}>
                {loadingEventCodes ? '불러오는 중...' : '목록 새로고침'}
              </AdminActionButton>
              {loadingEventCodes ? (
                <div style={{ marginTop: '12px', color: 'var(--color-text-secondary)' }}>로딩 중...</div>
              ) : eventCodes.length > 0 ? (
                <>
                  <EventCodeList>
                    {pagedEventCodes.map((code) => {
                      const closed = !!code.isClosed || (code.activatedCount >= code.maxActivations);
                      return (
                        <EventCodeItem key={code.code}>
                          <div>
                            {code.name && (
                              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '4px' }}>
                                {code.name}
                              </div>
                            )}
                            <EventCodeHeader>
                              <EventCodeValue>{code.code}</EventCodeValue>
                              <EventCodeCopyButton onClick={() => copyToClipboard(code.code)}>
                                복사
                              </EventCodeCopyButton>
                            </EventCodeHeader>
                            <EventCodeMeta>
                              <span>플랜: {code.targetUserType === 'basic' ? '베이직' : code.targetUserType === 'standard' ? '스탠다드' : '프로'}</span>
                              <span>기간: {code.durationMonths}개월</span>
                              <ActivatedCountBlock>활성: {code.activatedCount}/{code.maxActivations}</ActivatedCountBlock>
                              <span>생성일: {code.createdAt ? new Date(code.createdAt).toLocaleDateString('ko-KR') : '-'}</span>
                            </EventCodeMeta>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                            <EventCodeStatus $closed={closed}>
                              {closed ? '마감' : '활성'}
                            </EventCodeStatus>
                            <EventCodeActionButton
                              onClick={() => handleToggleEventCode(code.code, !closed)}
                              disabled={eventCodeToggling.has(code.code)}
                            >
                              {eventCodeToggling.has(code.code) ? '처리 중...' : closed ? '재개' : '마감'}
                            </EventCodeActionButton>
                            <EventCodeDeleteButton
                              onClick={() => handleDeleteEventCode(code.code)}
                              disabled={eventCodeDeleting.has(code.code)}
                            >
                              {eventCodeDeleting.has(code.code) ? '삭제 중...' : '삭제'}
                            </EventCodeDeleteButton>
                          </div>
                        </EventCodeItem>
                      );
                    })}
                  </EventCodeList>
                  {totalEventCodePages > 1 && (
                    <PaginationContainer>
                      <PaginationButton
                        onClick={() => setEventCodeCurrentPage(Math.max(1, eventCodeCurrentPage - 1))}
                        disabled={eventCodeCurrentPage === 1}
                      >
                        ‹
                      </PaginationButton>
                      <PaginationInfo>
                        {eventCodeCurrentPage} / {totalEventCodePages}
                      </PaginationInfo>
                      <PaginationButton
                        onClick={() => setEventCodeCurrentPage(Math.min(totalEventCodePages, eventCodeCurrentPage + 1))}
                        disabled={eventCodeCurrentPage === totalEventCodePages}
                      >
                        ›
                      </PaginationButton>
                    </PaginationContainer>
                  )}
                </>
              ) : (
                <div style={{ marginTop: '12px', color: 'var(--color-text-secondary)' }}>등록된 이벤트 코드가 없습니다.</div>
              )}
            </ListModalBody>
          </ListModalContent>
        </ListModalOverlay>
      )}

      {/* 그룹 이용권 목록 모달 */}
      {showGroupCouponListModal && (
        <ListModalOverlay onClick={() => setShowGroupCouponListModal(false)}>
          <ListModalContent onClick={(e) => e.stopPropagation()}>
            <ListModalHeader>
              <ListModalTitle>그룹 이용권 목록</ListModalTitle>
              <TermsModalCloseButton onClick={() => setShowGroupCouponListModal(false)}>×</TermsModalCloseButton>
            </ListModalHeader>
            <ListModalBody>
              <Description style={{ marginTop: 0 }}>
                발급된 그룹 이용권 배치 현황을 확인하고, 환불/재발송을 관리할 수 있습니다.
              </Description>
              <AdminActionButton onClick={loadGroupCouponBatches} disabled={loadingGroupCouponBatches}>
                {loadingGroupCouponBatches ? '불러오는 중...' : '목록 새로고침'}
              </AdminActionButton>
              {loadingGroupCouponBatches ? (
                <div style={{ marginTop: '12px', color: 'var(--color-text-secondary)' }}>로딩 중...</div>
              ) : groupCouponBatches.length > 0 ? (
                <>
                  <EventCodeList>
                    {pagedGroupCouponBatches.map((batch) => {
                      const today = new Date();
                      const y = today.getFullYear();
                      const m = String(today.getMonth() + 1).padStart(2, '0');
                      const d = String(today.getDate()).padStart(2, '0');
                      const todayYmd = `${y}-${m}-${d}`;
                      const refundExpired = !batch.refundEligibleUntilYmd || todayYmd > batch.refundEligibleUntilYmd;
                      const quantity = Number(batch.quantity || 0);
                      const activatedCount = Number(batch.activatedCount || 0);
                      const refundedCount = Number(batch.refundedCount || 0);
                      const refundableCount = Math.max(0, quantity - activatedCount - refundedCount);
                      const refundableLicenseCount = Math.max(0, quantity - refundedCount);
                      const isRefunded = refundedCount >= quantity || batch.status === 'REFUNDED';
                      const canRefund = !refundExpired && refundableLicenseCount > 0 && !isRefunded;
                      const canResend = refundableCount > 0;

                      return (
                        <EventCodeItem key={batch.batchId}>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '4px' }}>
                              그룹명: {batch.groupName || '-'}
                            </div>
                            <EventCodeHeader>
                              <EventCodeValue>{batch.batchId}</EventCodeValue>
                              <EventCodeCopyButton onClick={() => copyToClipboard(batch.batchId)}>
                                복사
                              </EventCodeCopyButton>
                            </EventCodeHeader>
                            <EventCodeMeta>
                              <span>이메일: {batch.targetEmail || '-'}</span>
                              <span>그룹 코드: {batch.groupCode || '-'}</span>
                              <span>플랜: {batch.planType === 'basic' ? '베이직' : batch.planType === 'standard' ? '스탠다드' : '프로'}</span>
                              <span>기간: {batch.termMonths}개월</span>
                              <span>이용자 수: {quantity}명</span>
                              <ActivatedCountBlock>활성: {activatedCount} / {quantity}</ActivatedCountBlock>
                              <span>환불 가능 기한: {batch.refundEligibleUntilYmd || '-'}</span>
                              <span>생성일: {batch.createdAt ? new Date(batch.createdAt).toLocaleDateString('ko-KR') : '-'}</span>
                            </EventCodeMeta>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                            <EventCodeStatus $closed={batch.status === 'REFUNDED'}>
                              {batch.status === 'REFUNDED' ? '환불완료' : (batch.status || '발급')}
                            </EventCodeStatus>
                            <EventCodeActionButton
                              onClick={() => canResend && handleOpenResendModal(batch)}
                              disabled={!canResend || groupCouponResending.has(batch.batchId)}
                            >
                              {groupCouponResending.has(batch.batchId) ? '재발송 중...' : canResend ? '그룹 코드 재발송' : '재발송 불가'}
                            </EventCodeActionButton>
                            <EventCodeActionButton
                              onClick={() => handleRefundGroupCouponBatch(batch.batchId)}
                              disabled={!canRefund || groupCouponRefunding.has(batch.batchId)}
                            >
                              {groupCouponRefunding.has(batch.batchId) ? '환불 처리 중...' : canRefund ? '환불' : '환불 불가'}
                            </EventCodeActionButton>
                          </div>
                        </EventCodeItem>
                      );
                    })}
                  </EventCodeList>
                  {totalGroupCouponPages > 1 && (
                    <PaginationContainer>
                      <PaginationButton
                        onClick={() => setGroupCouponCurrentPage(Math.max(1, groupCouponCurrentPage - 1))}
                        disabled={groupCouponCurrentPage === 1}
                      >
                        ‹
                      </PaginationButton>
                      <PaginationInfo>
                        {groupCouponCurrentPage} / {totalGroupCouponPages}
                      </PaginationInfo>
                      <PaginationButton
                        onClick={() => setGroupCouponCurrentPage(Math.min(totalGroupCouponPages, groupCouponCurrentPage + 1))}
                        disabled={groupCouponCurrentPage === totalGroupCouponPages}
                      >
                        ›
                      </PaginationButton>
                    </PaginationContainer>
                  )}
                </>
              ) : (
                <div style={{ marginTop: '12px', color: 'var(--color-text-secondary)' }}>발급된 그룹 이용권 배치가 없습니다.</div>
              )}
            </ListModalBody>
          </ListModalContent>
        </ListModalOverlay>
      )}

      {/* 그룹 코드 재발송 모달 */}
      {resendModalBatch && (
        <ResendModalOverlay onClick={handleCloseResendModal}>
          <ResendModalContent onClick={(e) => e.stopPropagation()}>
            <ResendModalTitle>그룹 코드 재발송</ResendModalTitle>
            <AdminInputLabel style={{ marginTop: 16 }}>이메일 주소</AdminInputLabel>
            <AdminInput
              type="email"
              value={resendModalEmail}
              onChange={(e) => setResendModalEmail(e.target.value)}
              placeholder="example@email.com"
              style={{ marginBottom: 24 }}
            />
            <ResendModalButtons>
              <ResendModalButton onClick={handleCloseResendModal}>닫기</ResendModalButton>
              <ResendModalButton
                $primary
                onClick={() => handleResendGroupCouponBatch(resendModalBatch.batchId, resendModalEmail)}
                disabled={groupCouponResending.has(resendModalBatch.batchId)}
              >
                {groupCouponResending.has(resendModalBatch.batchId) ? '재발송 중...' : '재발송'}
              </ResendModalButton>
            </ResendModalButtons>
          </ResendModalContent>
        </ResendModalOverlay>
      )}

      {/* 사용자 통계 세그먼트별 목록 */}
      {userStatsListModal && (
        <ListModalOverlay
          onClick={() => {
            if (licenseHistoryModal) setLicenseHistoryModal(null);
            else closeUserStatsListModal();
          }}
        >
          <ListModalContent onClick={(e) => e.stopPropagation()}>
            <ListModalHeader>
              <ListModalTitle>{userStatsListModal.title}</ListModalTitle>
              <TermsModalCloseButton
                onClick={() => {
                  setLicenseHistoryModal(null);
                  closeUserStatsListModal();
                }}
              >
                ×
              </TermsModalCloseButton>
            </ListModalHeader>
            <ListModalBody>
              <Description style={{ marginTop: 0 }}>
                통계에 포함된 사용자입니다. 이름·이메일·사용자 ID·플랜·이용 기간에 대해 아래에서 검색할 수 있습니다.
                {userStatsListModal.fromCache && (
                  <span style={{ display: 'block', marginTop: 8, color: 'var(--color-accent-text)', fontWeight: 600 }}>
                    · 브라우저에 저장된 목록을 표시 중입니다. 동일 세그먼트를 다시 열면 서버 조회 없이 표시됩니다(최대 30분).
                  </span>
                )}
              </Description>
              <UserStatsReloadSection>
                <AdminActionButton
                  type="button"
                  onClick={() => refreshUserStatsListFromServer(userStatsListModal.segment)}
                  disabled={userStatsListModal.loading || userStatsListRefreshing}
                >
                  {userStatsListRefreshing ? '서버에서 불러오는 중...' : '서버에서 다시 불러오기'}
                </AdminActionButton>
                <UserStatsReloadHint>
                  최신 데이터가 필요할 때만 눌러 주세요(전체 사용자 스캔·과금 발생).
                </UserStatsReloadHint>
              </UserStatsReloadSection>
              <UserStatsSearchSection>
                <UserStatsSearchInput
                  type="search"
                  placeholder="찾기 (이름, 이메일, ID, 플랜, 기간 등)"
                  value={userStatsListSearch}
                  onChange={(e) => setUserStatsListSearch(e.target.value)}
                  aria-label="목록 내 검색"
                />
                {!userStatsListModal.loading && (
                  <UserStatsSearchCount>
                    표시 {filteredUserStatsMembers.length}명 / 전체 {userStatsListModal.members.length}명
                  </UserStatsSearchCount>
                )}
              </UserStatsSearchSection>
              {userStatsListModal.loading ? (
                <div style={{ color: 'var(--color-text-secondary)', padding: '24px 0' }}>불러오는 중...</div>
              ) : filteredUserStatsMembers.length === 0 ? (
                <div style={{ color: 'var(--color-text-secondary)' }}>
                  {userStatsListModal.members.length === 0
                    ? '해당 구간에 해당하는 사용자가 없습니다.'
                    : '검색 조건에 맞는 사용자가 없습니다.'}
                </div>
              ) : (
                <UserStatsMemberList>
                  {filteredUserStatsMembers.map((m) => (
                    <UserStatsMemberCard key={m.id}>
                      <UserStatsMemberFields>
                        <UserStatsMemberRow>
                          <UserStatsPrimaryText>{m.name || '-'}</UserStatsPrimaryText>
                          <UserStatsMutedText>{m.email || '-'}</UserStatsMutedText>
                          <UserStatsMutedText>(사용자 ID) {m.id}</UserStatsMutedText>
                        </UserStatsMemberRow>
                        <UserStatsMemberRow>
                          <UserStatsAccentText>{m.planTypeLabel}</UserStatsAccentText>
                          <UserStatsAccentText>
                            {m.planPeriodLabel && m.planPeriodLabel !== '-'
                              ? m.planPeriodLabel
                              : '이용권 이용 기간 없음'}
                          </UserStatsAccentText>
                          <UserStatsSpacer aria-hidden />
                        </UserStatsMemberRow>
                      </UserStatsMemberFields>
                      <LicenseDetailButton type="button" onClick={() => setLicenseHistoryModal(m)}>
                        라이센스 상세조회
                      </LicenseDetailButton>
                    </UserStatsMemberCard>
                  ))}
                </UserStatsMemberList>
              )}
            </ListModalBody>
          </ListModalContent>
        </ListModalOverlay>
      )}

      {/* 라이센스 누적 이력 (관리자 통계 모달에서 열림) */}
      {licenseHistoryModal && (
        <NestedListModalOverlay onClick={() => setLicenseHistoryModal(null)}>
          <ListModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 960 }}>
            <ListModalHeader>
              <ListModalTitle>
                라이센스 이력 — {licenseHistoryModal.name || licenseHistoryModal.email || licenseHistoryModal.id}
              </ListModalTitle>
              <TermsModalCloseButton onClick={() => setLicenseHistoryModal(null)}>×</TermsModalCloseButton>
            </ListModalHeader>
            <ListModalBody>
              <Description style={{ marginTop: 0 }}>
                Firestore에 저장된 <strong>licenses</strong> 배열 기준 누적 구매·활성화 이력입니다.
              </Description>
              {!licenseHistoryModal.licenses || licenseHistoryModal.licenses.length === 0 ? (
                <div style={{ marginTop: 16, color: 'var(--color-text-secondary)' }}>저장된 라이센스 이력이 없습니다.</div>
              ) : (
                <div style={{ marginTop: 12, overflowX: 'auto' }}>
                  <LicenseHistoryTable>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>paymentId</th>
                        <th>planType</th>
                        <th>duration</th>
                        <th>활성</th>
                        <th>활성화됨</th>
                        <th>환불</th>
                        <th>구매/시작</th>
                        <th>만료</th>
                        <th>금액</th>
                      </tr>
                    </thead>
                    <tbody>
                      {licenseHistoryModal.licenses.map((lic, idx) => (
                        <tr key={`${licenseHistoryModal.id}-${idx}-${String(lic.paymentId ?? idx)}`}>
                          <td>{idx + 1}</td>
                          <td>{formatAdminLicenseField(lic.paymentId)}</td>
                          <td>{formatAdminLicenseField(lic.planType)}</td>
                          <td>{formatAdminLicenseField(lic.duration)}</td>
                          <td>{formatAdminLicenseField(lic.isActive)}</td>
                          <td>{formatAdminLicenseField(lic.isActivated)}</td>
                          <td>{formatAdminLicenseField(lic.isRefunded)}</td>
                          <td>
                            {formatAdminLicenseField(lic.purchasedAt ?? lic.periodStart ?? lic.activatedAt)}
                          </td>
                          <td>{formatAdminLicenseField(lic.expiresAt)}</td>
                          <td>{formatAdminLicenseField(lic.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </LicenseHistoryTable>
                </div>
              )}
            </ListModalBody>
          </ListModalContent>
        </NestedListModalOverlay>
      )}
    </Container>
  );
};

// 공지사항 뷰 모달 스타일
const AnnouncementViewModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10003;
  padding: 20px;
`;

const AnnouncementViewContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  max-width: 800px;
  max-height: 90vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: var(--modal-shell-border);
  box-shadow: var(--modal-shadow-large);
`;

const AnnouncementViewHeader = styled.div`
  position: relative;
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid var(--color-border);
  text-align: center;
`;

const AnnouncementViewTitle = styled.h2`
  margin: 0;
  color: var(--color-text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  flex: 1;
`;

const AnnouncementViewDate = styled.div`
  color: var(--color-text-secondary);
  font-size: 0.9rem;
`;

const AnnouncementViewCloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  color: var(--color-text-primary);
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-bg);
    border-color: var(--status-delayed);
    color: var(--status-delayed);
  }
`;

const AnnouncementViewBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;

  & > div {
    line-height: 1.6;
    color: var(--color-text-primary);

    img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      margin: 16px 0;
    }

    a {
      color: var(--color-accent);
      text-decoration: underline;
    }

    ul, ol {
      margin: 16px 0;
      padding-left: 24px;
    }

    p {
      margin: 12px 0;
    }
  }
`;


export default AdminPage;